<template>
  <div class="templates-whatsapp-container">
    <!-- Header Fixo ao Scroll -->
    <div class="header-section">
      <b-card class="header-card">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div class="header-info mb-2 mb-md-0">
            <h2 class="page-title mb-1">
              <feather-icon
                icon="MessageSquareIcon"
                size="28"
                class="mr-2"
              />
              Templates do WhatsApp
            </h2>
            <p class="page-subtitle mb-0">
              Gerencie seus templates de mensagens aprovados pelo WhatsApp
            </p>
          </div>

          <div class="header-actions">
            <b-button
              variant="primary"
              size="lg"
              class="btn-create"
              @click="showCreateModal = true"
            >
              <feather-icon
                icon="PlusIcon"
                size="18"
                class="mr-1"
              />
              Criar Template
            </b-button>
          </div>
        </div>
      </b-card>
    </div>

    <!-- Filtros (Rolam com a página) -->
    <b-card class="filters-card">
      <b-row>
        <!-- Seletor de Conta -->
        <b-col
          md="3"
          class="mb-2 mb-md-0"
        >
          <b-form-group
            label="Conta do WhatsApp"
            label-for="account-select"
            class="mb-0"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="PhoneIcon"
                size="18"
                class="text-success mr-2"
              />
              <b-form-select
                id="account-select"
                v-model="chatSelecionado"
                :options="facebookAccountsLista.map(c => ({
                  value: c.chat_token,
                  text: c.name
                }))"
                :disabled="carregando"
                @change="buscaTemplates"
              >
                <template #first>
                  <b-form-select-option
                    :value="null"
                    disabled
                  >
                    {{ carregando ? 'Carregando...' : 'Selecione uma conta' }}
                  </b-form-select-option>
                </template>
              </b-form-select>
            </div>
          </b-form-group>
        </b-col>

        <!-- Filtro de Status -->
        <b-col md="3">
          <b-form-group
            label="Filtrar por Status"
            label-for="status-filter"
            class="mb-0"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="FilterIcon"
                size="18"
                class="text-primary mr-2"
              />
              <b-form-select
                id="status-filter"
                v-model="filterStatus"
                :options="statusOptions"
              />
            </div>
          </b-form-group>
        </b-col>

        <!-- Filtro de Categoria -->
        <b-col md="3">
          <b-form-group
            label="Filtrar por Categoria"
            label-for="category-filter"
            class="mb-0"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="TagIcon"
                size="18"
                class="text-warning mr-2"
              />
              <b-form-select
                id="category-filter"
                v-model="filterCategory"
                :options="categoryFilterOptions"
              />
            </div>
          </b-form-group>
        </b-col>

        <!-- Busca -->
        <b-col md="2">
          <b-form-group
            label="Buscar"
            label-for="search-input"
            class="mb-0"
          >
            <b-form-input
              id="search-input"
              v-model="searchQuery"
              placeholder="Nome..."
              type="search"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Conteúdo -->
    <div class="content-section">
      <!-- Loading State -->
      <div
        v-if="carregando"
        class="text-center py-5"
      >
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem;"
        />
        <p class="mt-3 text-muted">
          Carregando templates...
        </p>
      </div>

      <!-- Empty State -->
      <b-card
        v-else-if="!carregando && templates.length === 0"
        class="text-center py-5 empty-state"
      >
        <feather-icon
          icon="InboxIcon"
          size="64"
          class="text-muted mb-3"
        />
        <h4 class="text-muted">
          Nenhum template encontrado
        </h4>
        <p class="text-muted">
          Crie seu primeiro template para começar
        </p>
        <b-button
          variant="primary"
          @click="showCreateModal = true"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-1"
          />
          Criar Primeiro Template
        </b-button>
      </b-card>

      <!-- Templates Grid -->
      <div
        v-else
        class="templates-grid"
      >
        <b-card
          v-for="template in paginatedTemplates"
          :key="template.id"
          class="template-card"
          :class="[
            `status-${template.status.toLowerCase()}`,
            { 'pending-approval': template.status === 'PENDING' }
          ]"
          @click="viewTemplate(template)"
        >
          <!-- Overlay de Loading para templates em aprovação -->
          <div
            v-if="template.status === 'PENDING'"
            class="pending-overlay"
          >
            <div class="pending-content">
              <b-spinner
                variant="warning"
                class="mb-2"
              />
              <div class="pending-text">
                <strong>Em Aprovação</strong>
                <small class="d-block mt-1">Aguardando análise do WhatsApp</small>
              </div>
            </div>
          </div>

          <!-- Header do Card -->
          <div class="template-header">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div class="template-name">
                <h5 class="mb-1">
                  {{ template.name }}
                </h5>
                <div class="template-meta">
                  <b-badge
                    :variant="getStatusVariant(template.status)"
                    class="mr-1"
                  >
                    <feather-icon
                      :icon="getStatusIcon(template.status)"
                      size="12"
                      class="mr-50"
                    />
                    {{ getStatusText(template.status) }}
                  </b-badge>
                  <b-badge
                    variant="light-secondary"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="GlobeIcon"
                      size="12"
                      class="mr-50"
                    />
                    {{ getLanguageText(template.language) }}
                  </b-badge>
                  <b-badge variant="light-info">
                    <feather-icon
                      icon="TagIcon"
                      size="12"
                      class="mr-50"
                    />
                    {{ template.category }}
                  </b-badge>
                </div>

                <!-- Aviso para templates não aprovados -->
                <div
                  v-if="template.status !== 'APPROVED'"
                  class="mt-2"
                >
                  <small class="text-warning d-flex align-items-center">
                    <feather-icon
                      icon="AlertCircleIcon"
                      size="14"
                      class="mr-1"
                    />
                    <span>Não disponível para uso</span>
                  </small>
                </div>
              </div>

              <b-dropdown
                variant="link"
                no-caret
                right
                toggle-class="text-decoration-none p-0"
                @click.stop
              >
                <template #button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="18"
                    class="text-muted"
                  />
                </template>
                <b-dropdown-item @click.stop="viewTemplate(template)">
                  <feather-icon
                    icon="EyeIcon"
                    size="14"
                    class="mr-1"
                  />
                  Visualizar
                </b-dropdown-item>
                <b-dropdown-divider />
                <b-dropdown-item
                  variant="danger"
                  @click.stop="confirmDelete(template)"
                >
                  <feather-icon
                    icon="Trash2Icon"
                    size="14"
                    class="mr-1"
                  />
                  Deletar
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>

          <!-- Preview do Conteúdo -->
          <div class="template-content">
            <div class="whatsapp-preview">
              <!-- Preview para Carrossel -->
              <template v-if="template.layout === 'carousel' && template.carouselCards && template.carouselCards.length">
                <div class="preview-bubble mb-2">
                  <div class="template-preview-content">
                    <!-- Body do Carrossel -->
                    <div class="template-body-preview">
                      <p class="mb-0">
                        {{ template.body || template.content }}
                      </p>
                    </div>
                  </div>
                  <div class="message-time">
                    <small class="text-muted">{{ new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}</small>
                  </div>
                </div>

                <!-- Cards do Carrossel -->
                <div class="carousel-preview-compact">
                  <div class="carousel-scroll-horizontal">
                    <div
                      v-for="(card, idx) in template.carouselCards.slice(0, 3)"
                      :key="idx"
                      class="carousel-card-compact"
                    >
                      <div class="carousel-card-image">
                        <img
                          v-if="card.imagePreview || card.imageUrl"
                          :src="card.imagePreview || card.imageUrl"
                          alt="Produto"
                        >
                        <div
                          v-else
                          class="carousel-card-placeholder"
                        >
                          <feather-icon
                            icon="ImageIcon"
                            size="24"
                          />
                        </div>
                      </div>
                      <div class="carousel-card-action">
                        <feather-icon
                          icon="ExternalLinkIcon"
                          size="12"
                          class="mr-1"
                        />
                        <span class="text-truncate">{{ card.buttonText || 'Ver produto' }}</span>
                      </div>
                      <div
                        v-if="card.buttonUrl"
                        class="carousel-card-link"
                      >
                        <small class="text-muted text-truncate">{{ card.buttonUrl }}</small>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="template.carouselCards.length > 3"
                    class="carousel-count-badge"
                  >
                    +{{ template.carouselCards.length - 3 }} cards
                  </div>
                </div>
              </template>

              <!-- Preview Normal (não carrossel) -->
              <div
                v-else
                class="preview-bubble"
              >
                <!-- Header e Body juntos -->
                <div class="template-preview-content">
                  <!-- Header (se existir) -->
                  <div
                    v-if="template.headerType && template.headerType !== 'NONE'"
                    class="template-header-preview"
                  >
                    <strong
                      v-if="template.headerType === 'TEXT'"
                      class="d-block mb-2"
                    >
                      {{ template.headerText }}
                    </strong>
                    <img
                      v-else-if="template.headerType === 'IMAGE' && template.headerMediaUrl"
                      :src="template.headerMediaUrl"
                      alt="Header Image"
                      class="template-header-image"
                      @error="$event.target.style.display='none'"
                    >
                    <div
                      v-else-if="template.headerType === 'IMAGE' && !template.headerMediaUrl"
                      class="header-type-badge"
                    >
                      <feather-icon
                        icon="ImageIcon"
                        size="14"
                        class="mr-1"
                      />
                      Imagem
                    </div>
                    <video
                      v-else-if="template.headerType === 'VIDEO' && template.headerMediaUrl"
                      :src="template.headerMediaUrl"
                      class="template-header-video"
                      muted
                    />
                    <div
                      v-else-if="template.headerType === 'VIDEO' && !template.headerMediaUrl"
                      class="header-type-badge"
                    >
                      <feather-icon
                        icon="VideoIcon"
                        size="14"
                        class="mr-1"
                      />
                      Vídeo
                    </div>
                    <div
                      v-else-if="template.headerType === 'DOCUMENT'"
                      class="header-type-badge"
                    >
                      <feather-icon
                        icon="FileTextIcon"
                        size="14"
                        class="mr-1"
                      />
                      Documento
                    </div>
                  </div>

                  <!-- Body -->
                  <div class="template-body-preview">
                    <p class="mb-0">
                      {{ template.body || template.content }}
                    </p>
                  </div>
                </div>

                <!-- Footer (se existir) -->
                <div
                  v-if="template.footer"
                  class="template-footer-preview"
                >
                  <small class="text-muted">{{ template.footer }}</small>
                </div>

                <!-- Buttons (se existir) -->
                <div
                  v-if="template.buttonType && template.buttonType !== 'NONE'"
                  class="template-buttons-preview"
                >
                  <!-- Quick Reply Buttons -->
                  <template v-if="template.buttonType === 'QUICK_REPLY'">
                    <div
                      v-for="(button, index) in template.quickReplyButtons.filter(b => b.text)"
                      :key="index"
                      class="button-preview"
                    >
                      <feather-icon
                        icon="CornerDownRightIcon"
                        size="12"
                        class="mr-1"
                      />
                      {{ button.text }}
                    </div>
                  </template>

                  <!-- Call to Action - URL Button -->
                  <div v-if="template.buttonType === 'CALL_TO_ACTION' && template.urlButton && template.urlButton.text">
                    <div class="button-preview button-url">
                      <feather-icon
                        icon="ExternalLinkIcon"
                        size="12"
                        class="mr-1"
                      />
                      {{ template.urlButton.text }}
                    </div>
                    <div
                      v-if="template.urlButton && template.urlButton.url"
                      class="button-url-link"
                    >
                      <small>{{ getUrlButtonDisplay(template.urlButton) }}</small>
                    </div>
                  </div>

                  <!-- Call to Action - Phone Button -->
                  <div
                    v-if="template.buttonType === 'CALL_TO_ACTION' && template.phoneButton && template.phoneButton.text"
                    class="button-preview button-phone"
                  >
                    <feather-icon
                      icon="PhoneIcon"
                      size="12"
                      class="mr-1"
                    />
                    {{ template.phoneButton.text }}
                  </div>

                  <!-- Copiar chave Pix (ORDER_DETAILS) -->
                  <div
                    v-if="template.buttonType === 'ORDER_DETAILS'"
                    class="button-preview"
                  >
                    <feather-icon
                      icon="CopyIcon"
                      size="12"
                      class="mr-1"
                    />
                    Copiar chave Pix
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </div>

      <!-- Paginação -->
      <div
        v-if="!carregando && totalFiltered > 0"
        class="pagination-section"
      >
        <div class="d-flex justify-content-between align-items-center">
          <div class="pagination-info">
            <small class="text-muted">
              Mostrando {{ startItem }}-{{ endItem }} de {{ totalFiltered }} templates
            </small>
          </div>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalFiltered"
            :per-page="perPage"
            align="right"
            size="sm"
            class="mb-0"
          />
        </div>
      </div>
    </div>

    <!-- Modal de Criação - Step by Step -->
    <b-modal
      v-model="showCreateModal"
      title="Criar Novo Template do WhatsApp"
      size="xl"
      hide-footer
      centered
      body-class="p-0 modal-body-no-scroll"
      @hide="resetForm"
    >
      <template #modal-header="{ close }">
        <div class="w-100">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="modal-title mb-0">
              Criar Novo Template do WhatsApp
            </h5>
            <div class="d-flex align-items-center">
              <b-button
                variant="outline-info"
                size="sm"
                class="mr-2"
                @click="showPoliciesModal = true"
              >
                <feather-icon
                  icon="ShieldIcon"
                  size="14"
                  class="mr-1"
                />
                Políticas do WhatsApp
              </b-button>
              <button
                type="button"
                class="close"
                @click="close()"
              >
                ×
              </button>
            </div>
          </div>

          <!-- Stepper Progress (carrossel: 4 passos, sem Botões) -->
          <div class="template-stepper">
            <div
              v-for="(step, index) in stepsToDisplay"
              :key="index"
              class="stepper-item"
              :class="{
                'active': currentStep === getActualStepForDisplay(index + 1),
                'completed': currentStep > getActualStepForDisplay(index + 1),
                'accessible': getActualStepForDisplay(index + 1) <= maxAccessibleStep,
                'blocked': getActualStepForDisplay(index + 1) > maxAccessibleStep
              }"
              @click="getActualStepForDisplay(index + 1) <= maxAccessibleStep && goToStep(getActualStepForDisplay(index + 1))"
            >
              <div class="stepper-circle">
                <feather-icon
                  v-if="currentStep > getActualStepForDisplay(index + 1)"
                  icon="CheckIcon"
                  size="14"
                />
                <span v-else>{{ index + 1 }}</span>
              </div>
              <div class="stepper-label">
                <small>
                  {{ step.label }}
                  <span
                    v-if="getActualStepForDisplay(index + 1) === 1 && currentStep === 1"
                    class="sub-step-indicator"
                  >
                    {{ currentSubStep }}/{{ setupSubSteps.length }}
                  </span>
                </small>
              </div>
              <div
                v-if="index < stepsToDisplay.length - 1"
                class="stepper-line"
              >
                <div
                  class="stepper-line-progress"
                  :style="{ width: getStepProgressDisplay(index) + '%' }"
                />
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Overlay de Loading -->
      <div
        v-if="criandoTemplate"
        class="modal-loading-overlay"
      >
        <div class="loading-content">
          <b-spinner
            variant="primary"
            style="width: 3rem; height: 3rem;"
            class="mb-3"
          />
          <h5 class="text-white mb-2">
            Criando Template...
          </h5>
          <p class="text-white-50 mb-0">
            Enviando para aprovação do WhatsApp
          </p>
        </div>
      </div>

      <b-row
        class="no-gutters"
        style="max-height: 75vh; background-color: #f8f9fa;"
      >
        <!-- Formulário - Lado Esquerdo -->
        <b-col
          lg="7"
          class="p-3 border-right modal-form-scrollable"
        >
          <b-form @submit.prevent="handleStepSubmit">
            <!-- ETAPA 1: Nome/Utilidade/Tipo (com 3 sub-steps) -->
            <div v-show="currentStep === 1">
              <!-- Sub-step 1: Nome do Template -->
              <div v-show="currentSubStep === 1">
                <div class="step-header mb-4">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h4 class="step-title">
                        <feather-icon
                          icon="EditIcon"
                          size="24"
                          class="mr-2 text-primary"
                        />
                        Nome do Template
                      </h4>
                      <p class="text-muted mb-0">
                        <b-badge
                          variant="light-primary"
                          class="mr-2"
                        >
                          Passo 1/3
                        </b-badge>
                        Escolha um nome único e descritivo para seu template
                      </p>
                    </div>
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="clearCurrentStep"
                    >
                      <feather-icon
                        icon="TrashIcon"
                        size="14"
                        class="mr-1"
                      />
                      Limpar
                    </b-button>
                  </div>
                </div>

                <div class="form-section">
                  <b-form-group
                    label="Nome do Template"
                    label-for="template-name-step1"
                    description="Use apenas letras minúsculas, números e underscores. Ex: boas_vindas_cliente"
                  >
                    <b-form-input
                      id="template-name-step1"
                      v-model="newTemplate.name"
                      placeholder="ex: pedido_aprovado"
                      required
                      size="lg"
                      @input="formatTemplateName"
                    />
                  </b-form-group>
                </div>
              </div>

              <!-- Sub-step 2: Utilidade -->
              <div v-show="currentSubStep === 2">
                <div class="step-header mb-4">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h4 class="step-title">
                        <feather-icon
                          icon="TargetIcon"
                          size="24"
                          class="mr-2 text-primary"
                        />
                        Para que será utilizado?
                      </h4>
                      <p class="text-muted mb-0">
                        <b-badge
                          variant="light-primary"
                          class="mr-2"
                        >
                          Passo 2/3
                        </b-badge>
                        Selecione onde este template será utilizado no sistema
                      </p>
                    </div>
                  </div>
                </div>

                <div class="utilidade-options">
                  <div
                    v-for="opcao in templateUtilidades"
                    :key="opcao.value"
                    class="utilidade-card"
                    :class="{
                      'selected': newTemplate.utilidade === opcao.value,
                      [`border-${opcao.color}`]: newTemplate.utilidade === opcao.value
                    }"
                    @click="newTemplate.utilidade = opcao.value"
                  >
                    <div
                      class="utilidade-icon"
                      :class="`bg-light-${opcao.color}`"
                    >
                      <feather-icon
                        :icon="opcao.icon"
                        size="28"
                        :class="`text-${opcao.color}`"
                      />
                    </div>
                    <div class="utilidade-content">
                      <h5 class="mb-1">
                        {{ opcao.label }}
                      </h5>
                      <p class="text-muted mb-0 small">
                        {{ opcao.description }}
                      </p>
                    </div>
                    <div class="utilidade-check">
                      <feather-icon
                        v-if="newTemplate.utilidade === opcao.value"
                        icon="CheckCircleIcon"
                        size="24"
                        class="text-success"
                      />
                      <div
                        v-else
                        class="check-placeholder"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Sub-step 3: Tipo -->
              <div v-show="currentSubStep === 3">
                <div class="step-header mb-4">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h4 class="step-title">
                        <feather-icon
                          icon="LayersIcon"
                          size="24"
                          class="mr-2 text-primary"
                        />
                        Tipo de Template
                      </h4>
                      <p class="text-muted mb-0">
                        <b-badge
                          variant="light-primary"
                          class="mr-2"
                        >
                          Passo 3/3
                        </b-badge>
                        Escolha como deseja criar seu template
                      </p>
                    </div>
                  </div>
                </div>

                <div class="tipo-options">
                  <div
                    v-for="tipo in templateTipos"
                    :key="tipo.value"
                    class="tipo-card"
                    :class="{
                      'selected': newTemplate.tipo === tipo.value,
                      [`border-${tipo.color}`]: newTemplate.tipo === tipo.value
                    }"
                    @click="newTemplate.tipo = tipo.value"
                  >
                    <div
                      class="tipo-icon"
                      :class="`bg-light-${tipo.color}`"
                    >
                      <feather-icon
                        :icon="tipo.icon"
                        size="32"
                        :class="`text-${tipo.color}`"
                      />
                    </div>
                    <div class="tipo-content">
                      <h5 class="mb-1">
                        {{ tipo.label }}
                      </h5>
                      <p class="text-muted mb-0">
                        {{ tipo.description }}
                      </p>
                    </div>
                    <div class="tipo-check">
                      <feather-icon
                        v-if="newTemplate.tipo === tipo.value"
                        icon="CheckCircleIcon"
                        size="24"
                        class="text-success"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ETAPA 2: Templates Pré-definidos OU Informações Básicas -->
            <div v-show="currentStep === 2">
              <!-- Step 2 Header -->
              <div class="step-header mb-4">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <h4 class="step-title">
                      <feather-icon
                        icon="InfoIcon"
                        size="24"
                        class="mr-2 text-primary"
                      />
                      <template v-if="newTemplate.tipo === 'predefinido'">
                        Escolher Template Pré-definido
                      </template>
                      <template v-else>
                        Informações Básicas
                      </template>
                    </h4>
                    <p class="text-muted mb-0">
                      <template v-if="newTemplate.tipo === 'predefinido'">
                        Selecione um modelo pronto e personalize conforme sua necessidade
                      </template>
                      <template v-else>
                        Preencha as informações do seu template customizado
                      </template>
                    </p>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="clearCurrentStep"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="14"
                      class="mr-1"
                    />
                    Limpar Campos
                  </b-button>
                </div>
              </div>

              <!-- Templates Pré-definidos (apenas se tipo = predefinido) -->
              <div
                v-if="newTemplate.tipo === 'predefinido'"
                class="form-section mb-3"
              >
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <div class="flex-grow-1">
                    <div class="d-flex align-items-center justify-content-between">
                      <h5 class="section-title mb-1">
                        <feather-icon
                          icon="LayoutIcon"
                          size="18"
                          class="mr-2"
                        />
                        Templates Pré-definidos
                      </h5>
                      <b-dropdown
                        variant="outline-primary"
                        size="sm"
                        right
                        class="filtro-categoria-dropdown"
                      >
                        <template #button-content>
                          <feather-icon
                            icon="FilterIcon"
                            size="14"
                            class="mr-1"
                          />
                          {{ filtroCategoriaLabel }}
                        </template>
                        <b-dropdown-item @click="filtroCategoriaPredefinida = null">
                          <feather-icon
                            icon="ListIcon"
                            size="14"
                            class="mr-2"
                          />
                          Todas as Categorias
                        </b-dropdown-item>
                        <b-dropdown-divider />
                        <b-dropdown-item @click="filtroCategoriaPredefinida = 'UTILITY'">
                          <b-badge
                            variant="success"
                            class="mr-2"
                          >
                            UTILITY
                          </b-badge>
                          Utilitários
                        </b-dropdown-item>
                        <b-dropdown-item @click="filtroCategoriaPredefinida = 'MARKETING'">
                          <b-badge
                            variant="warning"
                            class="mr-2"
                          >
                            MARKETING
                          </b-badge>
                          Marketing
                        </b-dropdown-item>
                      </b-dropdown>
                    </div>
                    <small class="text-muted d-block">
                      Clique em um template para preencher automaticamente
                    </small>
                  </div>
                </div>

                <div class="predefined-templates-grid">
                  <div
                    v-for="(template, index) in predefinedTemplatesFiltered"
                    :key="index"
                    class="predefined-card"
                    @click="loadPredefinedTemplate(template)"
                  >
                    <div class="predefined-header">
                      <feather-icon
                        :icon="getTemplateIcon(template)"
                        size="20"
                        class="text-primary"
                      />
                      <b-badge
                        :variant="template.category === 'MARKETING' ? 'warning' : 'success'"
                        class="ml-auto"
                      >
                        {{ template.category }}
                      </b-badge>
                    </div>
                    <h6 class="predefined-name">
                      {{ formatTemplateName(template.name) }}
                    </h6>
                    <small class="predefined-description text-muted">
                      {{ template.description }}
                    </small>
                  </div>
                </div>
              </div>

              <!-- Informações Básicas (Categoria e Idioma) - APENAS se tipo = customizado -->
              <div
                v-if="newTemplate.tipo === 'customizado'"
                class="form-section mb-4"
              >
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <h5 class="section-title mb-0">
                    <feather-icon
                      icon="SettingsIcon"
                      size="18"
                      class="mr-2"
                    />
                    Configurações do Template
                  </h5>
                </div>

                <b-row>
                  <b-col md="6">
                    <b-form-group
                      label="Categoria"
                      label-for="template-category"
                    >
                      <b-form-select
                        id="template-category"
                        v-model="newTemplate.category"
                        :options="categoryOptions"
                        required
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="Idioma"
                      label-for="template-language"
                    >
                      <b-form-select
                        id="template-language"
                        v-model="newTemplate.language"
                        :options="languageOptions"
                        required
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>
            </div>

            <!-- ETAPA 3: Conteúdo (Header + Mensagem + Rodapé ou Carrossel) -->
            <div v-show="currentStep === 3">
              <div class="step-header mb-4">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <h4 class="step-title">
                      <feather-icon
                        icon="FileTextIcon"
                        size="24"
                        class="mr-2 text-primary"
                      />
                      Conteúdo da Mensagem
                    </h4>
                    <p class="text-muted mb-0">
                      {{ newTemplate.layout === 'carousel' ? 'Configure o corpo e os cards do carrossel' : 'Configure o header, corpo e rodapé da mensagem' }}
                    </p>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="clearCurrentStep"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="14"
                      class="mr-1"
                    />
                    Limpar Campos
                  </b-button>
                </div>
              </div>

              <!-- Layout (apenas template customizado) -->
              <div
                v-if="newTemplate.tipo === 'customizado'"
                class="form-section mb-4"
              >
                <h5 class="section-subtitle mb-3">
                  <feather-icon
                    icon="LayoutIcon"
                    size="18"
                    class="mr-2"
                  />
                  Tipo de layout
                </h5>
                <b-form-group label="Layout do template">
                  <b-form-select
                    v-model="newTemplate.layout"
                    :options="layoutOptions"
                  />
                </b-form-group>
              </div>

              <!-- Conteúdo CARROSSEL -->
              <template v-if="newTemplate.tipo === 'customizado' && newTemplate.layout === 'carousel'">
                <!-- Corpo (obrigatório) - sem variáveis -->
                <div class="form-section mb-4">
                  <h5 class="section-subtitle mb-3">
                    <feather-icon
                      icon="MessageSquareIcon"
                      size="18"
                      class="mr-2"
                    />
                    Corpo da Mensagem (Obrigatório)
                  </h5>
                  <b-alert
                    show
                    variant="info"
                    class="mb-3"
                  >
                    <div class="d-flex align-items-start">
                      <feather-icon
                        icon="InfoIcon"
                        size="18"
                        class="mr-2 mt-1 flex-shrink-0"
                      />
                      <div>
                        <strong>Por enquanto não é possível utilizar variáveis neste tipo de template.</strong>
                        <p
                          class="mb-0 mt-1 small"
                          v-text="avisoCarrosselSemVariaveis"
                        />
                      </div>
                    </div>
                  </b-alert>
                  <b-form-group
                    label="Conteúdo"
                    :state="bodyValidationMessageCarousel ? false : null"
                  >
                    <b-form-textarea
                      ref="bodyTextareaCarousel"
                      v-model="newTemplate.body"
                      rows="6"
                      placeholder="Digite o conteúdo (apenas texto fixo, sem variáveis)..."
                      required
                      maxlength="1028"
                      :state="bodyValidationMessageCarousel ? false : null"
                    />
                    <small class="text-muted">
                      {{ (newTemplate.body || '').length }}/1028 caracteres
                    </small>
                    <small
                      v-if="bodyValidationMessageCarousel"
                      class="text-danger d-block mt-1"
                    >
                      {{ bodyValidationMessageCarousel }}
                    </small>
                  </b-form-group>
                </div>

                <!-- Botão do carrossel (mesmo texto em todos os cards; base e sufixo extraídos dos links) -->
                <div class="form-section mb-4">
                  <h5 class="section-subtitle mb-3">
                    <feather-icon
                      icon="LinkIcon"
                      size="18"
                      class="mr-2"
                    />
                    Botão dos cards
                  </h5>
                  <b-form-group
                    label="Texto do botão (igual em todos os cards)"
                    label-for="carousel-btn-text"
                  >
                    <b-form-input
                      id="carousel-btn-text"
                      v-model="newTemplate.carouselButtonText"
                      placeholder="Ex: Acessar"
                      maxlength="25"
                    />
                    <small class="text-muted">Máx. 25 caracteres. A URL base e o sufixo de cada card são extraídos automaticamente dos links dos produtos.</small>
                  </b-form-group>
                </div>

                <!-- Cards do carrossel (2 a 10) -->
                <div class="form-section mb-4">
                  <div class="d-flex align-items-center justify-content-between mb-3">
                    <h5 class="section-subtitle mb-0">
                      <feather-icon
                        icon="ImageIcon"
                        size="18"
                        class="mr-2"
                      />
                      Cards do carrossel (mín. 2, máx. 10)
                    </h5>
                    <b-button
                      v-if="newTemplate.carouselCards.length < 10"
                      variant="outline-primary"
                      size="sm"
                      @click="addCarouselCard"
                    >
                      <feather-icon
                        icon="PlusIcon"
                        size="14"
                        class="mr-1"
                      />
                      Adicionar card
                    </b-button>
                  </div>

                  <!-- Aviso para Recomendação de Produtos: dados são exemplos para a Meta -->
                  <b-alert
                    v-if="isRecomendacaoProdutosTemplate"
                    show
                    variant="warning"
                    class="mb-3 recomendacao-produtos-aviso"
                  >
                    <div class="d-flex align-items-start">
                      <feather-icon
                        icon="AlertTriangleIcon"
                        size="20"
                        class="mr-2 mt-1 flex-shrink-0"
                      />
                      <div>
                        <strong class="d-block mb-1">Importante — Recomendação de Produtos</strong>
                        <p class="mb-0 small">
                          As imagens e links que você inserir aqui são <strong>informações de exemplo para aprovação na Meta (WhatsApp)</strong>.
                          Eles <strong>não são</strong> necessariamente os produtos que serão enviados na campanha.
                          Os produtos exibidos na recomendação <strong>mudam de acordo com o perfil de cada cliente</strong>, de forma personalizada.
                        </p>
                        <p class="mb-0 small mt-2">
                          Preencha com pelo menos 2 cards de exemplo para que o template seja aprovado; na hora do envio, o sistema escolherá os produtos conforme o cliente.
                        </p>
                      </div>
                    </div>
                  </b-alert>

                  <div class="carousel-cards-row">
                    <div
                      v-for="(card, index) in newTemplate.carouselCards"
                      :key="index"
                      class="carousel-card-editor-wrap"
                    >
                      <b-card class="carousel-card-editor h-100">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                          <strong>Card {{ index + 1 }}</strong>
                          <b-button
                            v-if="newTemplate.carouselCards.length > 2"
                            variant="outline-danger"
                            size="sm"
                            @click="removeCarouselCard(index)"
                          >
                            <feather-icon
                              icon="TrashIcon"
                              size="14"
                            />
                          </b-button>
                        </div>
                        <b-form-group
                          label="Imagem do produto"
                          class="mb-2"
                        >
                          <input
                            :ref="`carouselCardInput_${index}`"
                            :data-carousel-card-input="index"
                            type="file"
                            accept="image/jpeg,image/png"
                            class="d-none"
                            @change="e => handleCarouselCardImage(e, index)"
                          >
                          <div
                            v-if="!card.imagePreview"
                            class="upload-zone-carousel rounded border p-3 text-center"
                            @click="openCarouselCardFile(index)"
                          >
                            <feather-icon
                              icon="ImageIcon"
                              size="28"
                              class="text-primary mb-2"
                            />
                            <p class="mb-0 small">
                              Clique para enviar imagem
                            </p>
                            <small class="text-muted">JPG, PNG</small>
                          </div>
                          <div
                            v-else
                            class="carousel-card-preview rounded border overflow-hidden"
                          >
                            <img
                              :src="card.imagePreview"
                              alt="Preview"
                              class="img-fluid d-block"
                              style="max-height: 120px; object-fit: cover; width: 100%;"
                            >
                            <div class="p-2 bg-light d-flex justify-content-between align-items-center">
                              <small class="text-muted">Imagem enviada</small>
                              <b-button
                                variant="outline-danger"
                                size="sm"
                                @click="removeCarouselCardImage(index)"
                              >
                                Remover
                              </b-button>
                            </div>
                          </div>
                        </b-form-group>
                        <b-form-group
                          label="Link do produto"
                          label-for="carousel-product-link"
                        >
                          <b-form-input
                            :id="`carousel-product-link-${index}`"
                            v-model="card.productLink"
                            placeholder="https://www.seudominio.com.br/produto-x"
                            type="url"
                          />
                          <small class="text-muted">URL completa de um produto.</small>
                        </b-form-group>
                      </b-card>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Conteúdo SIMPLES (header + corpo + rodapé) -->
              <template v-if="newTemplate.tipo !== 'customizado' || newTemplate.layout === 'single'">
                <!-- Header -->
                <div class="form-section mb-4">
                  <h5 class="section-subtitle mb-3">
                    <feather-icon
                      icon="ImageIcon"
                      size="18"
                      class="mr-2"
                    />
                    Header (Opcional)
                  </h5>
                  <b-form-group label="Tipo de Header">
                    <b-form-select
                      v-model="newTemplate.headerType"
                      :options="headerTypeOptions"
                    />
                  </b-form-group>

                  <!-- Header TEXT -->
                  <b-form-group
                    v-if="newTemplate.headerType === 'TEXT'"
                    label="Texto do Header"
                    :state="headerTextState"
                  >
                    <b-form-input
                      v-model="newTemplate.headerText"
                      placeholder="Ex: Olá, seja bem-vindo"
                      maxlength="60"
                      :state="headerTextState"
                    />
                    <small :class="headerHasVariable ? 'text-danger' : 'text-muted'">
                      {{ (newTemplate.headerText || '').length }}/60 caracteres
                      <span
                        v-if="headerHasVariable"
                        class="d-block mt-1"
                      >Não é possível inserir variáveis no header.</span>
                      <template v-else>Máximo 60 caracteres. Não use variáveis (apenas texto fixo).</template>
                    </small>
                  </b-form-group>

                  <!-- Header MEDIA Upload -->
                  <div v-if="['IMAGE', 'VIDEO'].includes(newTemplate.headerType)">
                    <b-form-group
                      :label="`Arquivo ${newTemplate.headerType === 'IMAGE' ? 'Imagem' : 'Vídeo'}`"
                    >
                      <input
                        ref="headerMediaInput"
                        type="file"
                        :accept="newTemplate.headerType === 'IMAGE' ? 'image/jpeg,image/png' : 'video/mp4,video/avi'"
                        class="d-none"
                        @change="handleHeaderMediaUpload"
                      >

                      <div
                        v-if="!newTemplate.headerMediaPreview"
                        class="upload-zone-header"
                        @click="$refs.headerMediaInput.click()"
                      >
                        <feather-icon
                          :icon="newTemplate.headerType === 'IMAGE' ? 'ImageIcon' : 'VideoIcon'"
                          size="32"
                          class="text-primary mb-2"
                        />
                        <p class="mb-1">
                          <strong>Clique para fazer upload</strong>
                        </p>
                        <small class="text-muted">
                          {{ newTemplate.headerType === 'IMAGE' ? 'JPG, PNG até 5MB' : 'MP4, AVI até 16MB' }}
                        </small>
                      </div>

                      <div
                        v-else
                        class="media-preview-container-header"
                      >
                        <div class="media-preview-header">
                          <img
                            v-if="newTemplate.headerType === 'IMAGE'"
                            :src="newTemplate.headerMediaPreview"
                            alt="Preview"
                            class="preview-img-header"
                          >
                          <video
                            v-else-if="newTemplate.headerType === 'VIDEO'"
                            :src="newTemplate.headerMediaPreview"
                            controls
                            class="preview-video-header"
                          />
                        </div>
                        <div class="media-actions-header mt-2">
                          <b-button
                            variant="outline-danger"
                            size="sm"
                            @click="removeHeaderMedia"
                          >
                            <feather-icon
                              icon="TrashIcon"
                              size="14"
                              class="mr-1"
                            />
                            Remover
                          </b-button>
                          <b-button
                            variant="outline-primary"
                            size="sm"
                            @click="$refs.headerMediaInput.click()"
                          >
                            <feather-icon
                              icon="RefreshCwIcon"
                              size="14"
                              class="mr-1"
                            />
                            Trocar
                          </b-button>
                        </div>
                      </div>

                      <b-alert
                        v-if="newTemplate.headerType === 'IMAGE' || newTemplate.headerType === 'VIDEO'"
                        show
                        variant="warning"
                        class="mt-2 mb-0"
                      >
                        <small>
                          <feather-icon
                            icon="AlertCircleIcon"
                            size="14"
                            class="mr-1"
                          />
                          <strong>Obrigatório:</strong> O arquivo será validado e enviado ao criar o template
                        </small>
                      </b-alert>
                    </b-form-group>
                  </div>
                </div>

                <!-- Corpo da Mensagem -->
                <div class="form-section mb-4">
                  <h5 class="section-subtitle mb-3">
                    <feather-icon
                      icon="MessageSquareIcon"
                      size="18"
                      class="mr-2"
                    />
                    Corpo da Mensagem (Obrigatório)
                  </h5>
                  <b-form-group label="Conteúdo">
                    <b-form-textarea
                      ref="bodyTextarea"
                      v-model="newTemplate.body"
                      rows="8"
                      placeholder="Digite o conteúdo... Clique nas variáveis abaixo para inserir"
                      required
                      maxlength="1028"
                      :state="bodyValidationMessage ? false : null"
                    />
                    <small class="text-muted">
                      {{ (newTemplate.body || '').length }}/1028 caracteres
                    </small>
                    <small
                      v-if="bodyValidationMessage"
                      class="text-danger d-block mt-1"
                    >
                      {{ bodyValidationMessage }}
                    </small>
                  </b-form-group>

                  <!-- Botões de Variáveis Clicáveis -->
                  <div class="variables-buttons-container mb-2">
                    <div class="d-flex align-items-center mb-2">
                      <feather-icon
                        icon="CodeIcon"
                        size="16"
                        class="text-primary mr-2"
                      />
                      <strong class="text-primary">Clique para inserir variáveis:</strong>
                    </div>
                    <div class="variables-grid">
                      <b-button
                        v-for="variable in availableVariablesForBody"
                        :key="variable.key"
                        variant="outline-primary"
                        size="sm"
                        class="variable-btn"
                        @click="insertVariable(variable.key)"
                      >
                        <feather-icon
                          icon="PlusCircleIcon"
                          size="12"
                          class="mr-1"
                        />
                        {{ variable.label }}
                      </b-button>
                    </div>
                  </div>

                  <!-- Alerta de Limitações -->
                  <b-alert
                    show
                    variant="warning"
                    class="mb-0"
                  >
                    <div class="d-flex align-items-start">
                      <feather-icon
                        icon="AlertTriangleIcon"
                        size="14"
                        class="mr-2 mt-1 flex-shrink-0"
                      />
                      <div>
                        <small>
                          <strong>Importante:</strong> As variáveis automáticas (como código de rastreio ou data) não aparecem quando a mensagem é enviada pela Chat com Clientes (Central de Atendimento) Em campanhas. apenas <code v-text="'{{primeiro_nome}}'" />funciona corretamente.
                        </small>
                      </div>
                    </div>
                  </b-alert>
                </div>

                <!-- Rodapé -->
                <div class="form-section mb-4">
                  <h5 class="section-subtitle mb-3">
                    <feather-icon
                      icon="AlignCenterIcon"
                      size="18"
                      class="mr-2"
                    />
                    Rodapé (Opcional)
                  </h5>
                  <b-form-group
                    label="Texto do Rodapé"
                    :state="footerState"
                  >
                    <b-form-input
                      v-model="newTemplate.footer"
                      placeholder="Ex: Atenciosamente, Equipe de Suporte"
                      maxlength="60"
                      :state="footerState"
                    />
                    <small :class="footerHasVariable ? 'text-danger' : 'text-muted'">
                      {{ (newTemplate.footer || '').length }}/60 caracteres
                      <span
                        v-if="footerHasVariable"
                        class="d-block mt-1"
                      >Não é possível inserir variáveis no rodapé.</span>
                      <template v-else>Máximo 60 caracteres. Não use variáveis (apenas texto fixo). Deixe em branco se não quiser rodapé.</template>
                    </small>
                  </b-form-group>
                </div>
              </template>
            </div>

            <!-- ETAPA 4: Buttons (oculta para carrossel) -->
            <div v-show="currentStep === 4 && newTemplate.layout !== 'carousel'">
              <div class="step-header mb-4">
                <div class="d-flex justify-content-between align-items-start">
                  <div>
                    <h4 class="step-title">
                      <feather-icon
                        icon="MousePointerIcon"
                        size="24"
                        class="mr-2 text-primary"
                      />
                      Botões (Opcional)
                    </h4>
                    <p class="text-muted mb-0">
                      Adicione botões interativos à sua mensagem
                    </p>
                  </div>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    @click="clearCurrentStep"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="14"
                      class="mr-1"
                    />
                    Limpar Campos
                  </b-button>
                </div>
              </div>

              <div class="form-section mb-4">
                <b-form-group label="Tipo de Botões">
                  <b-form-select
                    v-model="newTemplate.buttonType"
                    :options="buttonTypeOptions"
                  />
                </b-form-group>

                <!-- Quick Reply Buttons -->
                <div v-if="newTemplate.buttonType === 'QUICK_REPLY'">
                  <div
                    v-for="(button, index) in newTemplate.quickReplyButtons"
                    :key="index"
                    class="mb-2"
                  >
                    <b-input-group>
                      <b-form-input
                        v-model="button.text"
                        :placeholder="`Botão ${index + 1}`"
                        maxlength="25"
                      />
                      <b-input-group-append>
                        <b-button
                          v-if="newTemplate.quickReplyButtons.length > 1"
                          variant="outline-danger"
                          @click="removeQuickReplyButton(index)"
                        >
                          <feather-icon
                            icon="XIcon"
                            size="14"
                          />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </div>
                  <b-button
                    v-if="newTemplate.quickReplyButtons.length < 3"
                    variant="outline-primary"
                    size="sm"
                    @click="addQuickReplyButton"
                  >
                    <feather-icon
                      icon="PlusIcon"
                      size="14"
                      class="mr-1"
                    />
                    Adicionar Botão
                  </b-button>
                  <small class="text-muted d-block mt-1">Máximo 3 botões, 25 caracteres cada</small>
                </div>

                <!-- Call to Action Buttons -->
                <div v-if="newTemplate.buttonType === 'CALL_TO_ACTION'">
                  <div class="mb-3">
                    <label class="font-weight-bold">Botão de URL</label>
                    <b-form-group
                      label="Texto do Botão"
                      class="mb-2"
                    >
                      <b-form-input
                        v-model="newTemplate.urlButton.text"
                        placeholder="Ex: Ver site"
                        maxlength="25"
                      />
                    </b-form-group>
                    <b-form-group
                      label="Tipo de URL"
                      class="mb-2"
                    >
                      <b-form-select
                        v-if="urlButtonTypeOptions.length > 1"
                        v-model="newTemplate.urlButton.type"
                        :options="urlButtonTypeOptions"
                      />
                      <p
                        v-else
                        class="mb-0 form-control-plaintext text-muted small"
                      >
                        URL Estática (fixa) — único tipo permitido para campanhas
                      </p>
                    </b-form-group>
                    <b-form-group
                      label="URL"
                      class="mb-2"
                    >
                      <b-form-input
                        v-model="newTemplate.urlButton.url"
                        :placeholder="newTemplate.urlButton.type === 'static' ? 'https://seusite.com/pagina' : '{{link_de_rastreio}}'"
                        type="url"
                      />
                      <small
                        v-if="newTemplate.urlButton.type === 'dynamic'"
                        class="text-muted d-block"
                      >
                        Use variáveis descritivas como <code v-text="'{{link_de_rastreio}}'" />, <code v-text="'{{link_de_avaliacao}}'" />, <code v-text="'{{link_do_boleto}}'" />, <code v-text="'{{link_do_carrinho}}'" />
                      </small>
                      <small
                        v-else
                        class="text-muted"
                      >
                        URL estática: link fixo sem variáveis (ex: https://seusite.com/contato)
                      </small>
                    </b-form-group>

                    <b-alert
                      v-if="urlButtonUtmSuggested"
                      show
                      variant="info"
                      class="mb-3"
                    >
                      <div class="d-flex align-items-start">
                        <feather-icon
                          icon="InfoIcon"
                          size="16"
                          class="mr-2 mt-1 flex-shrink-0"
                        />
                        <div class="flex-grow-1">
                          <strong class="d-block mb-1">UTM de rastreio será adicionada automaticamente</strong>
                          <small class="d-block">
                            Nenhuma UTM detectada na URL. Os parâmetros abaixo serão incluídos ao salvar o template para garantir o rastreio:
                          </small>
                          <code class="d-block mt-1 small text-break">{{ urlButtonUtmSuggested }}</code>
                        </div>
                      </div>
                    </b-alert>

                    <b-alert
                      v-if="newTemplate.urlButton.type === 'dynamic'"
                      show
                      variant="info"
                      class="mb-3"
                    >
                      <div class="d-flex align-items-start">
                        <feather-icon
                          icon="InfoIcon"
                          size="16"
                          class="mr-2 mt-1 flex-shrink-0"
                        />
                        <div>
                          <strong class="d-block mb-1">URLs Dinâmicas com Variáveis Descritivas</strong>
                          <small>
                            Use variáveis nomeadas para links dinâmicos:<br>
                            • <code v-text="'{{link_de_rastreio}}'" /> - Link de rastreamento do pedido<br>
                            • <code v-text="'{{link_de_avaliacao}}'" /> - Link para avaliar o pedido<br>
                            • <code v-text="'{{link_do_boleto}}'" /> - Link para acessar o boleto<br>
                            • <code v-text="'{{link_do_carrinho}}'" /> - Link do carrinho abandonado<br><br>
                          </small>
                        </div>
                      </div>
                    </b-alert>

                    <!-- Variáveis Rápidas para URL Dinâmica -->
                    <div
                      v-if="newTemplate.urlButton.type === 'dynamic'"
                      class="mb-3"
                    >
                      <label class="d-block mb-2">
                        <small class="font-weight-bold">Variáveis Rápidas para URL:</small>
                      </label>
                      <div class="d-flex flex-wrap gap-2">
                        <b-button
                          v-for="variable in urlVariables"
                          :key="variable.key"
                          variant="outline-primary"
                          size="sm"
                          @click="insertUrlVariable(variable.key)"
                        >
                          <feather-icon
                            icon="LinkIcon"
                            size="12"
                            class="mr-1"
                          />
                          {{ variable.label }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Copiar chave Pix (ORDER_DETAILS - texto fixo, não editável) -->
                <div v-if="newTemplate.buttonType === 'ORDER_DETAILS'">
                  <b-alert
                    show
                    variant="info"
                    class="mb-0"
                  >
                    <div class="d-flex align-items-center">
                      <feather-icon
                        icon="CopyIcon"
                        size="18"
                        class="mr-2"
                      />
                      <span>Botão <strong>Copiar chave Pix</strong> (texto fixo enviado à API). Não é possível alterar.</span>
                    </div>
                  </b-alert>
                </div>
              </div>
            </div>

            <!-- ETAPA 5: Preview e Confirmação -->
            <div v-show="currentStep === 5">
              <div class="step-header mb-4">
                <h4 class="step-title">
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="24"
                    class="mr-2 text-success"
                  />
                  Revisar e Confirmar
                </h4>
                <p class="text-muted">
                  Verifique todas as informações antes de enviar para aprovação
                </p>
              </div>

              <div class="form-section">
                <div class="review-section">
                  <h5 class="mb-3">
                    📋 Resumo do Template
                  </h5>

                  <div class="review-item mb-3">
                    <label class="font-weight-bold d-block mb-1">Nome:</label>
                    <p class="mb-0">
                      {{ newTemplate.name || '(não definido)' }}
                    </p>
                  </div>

                  <b-row>
                    <b-col md="6">
                      <div class="review-item mb-3">
                        <label class="font-weight-bold d-block mb-1">Categoria:</label>
                        <b-badge :variant="newTemplate.category === 'MARKETING' ? 'warning' : 'success'">
                          {{ newTemplate.category }}
                        </b-badge>
                      </div>
                    </b-col>
                    <b-col md="6">
                      <div class="review-item mb-3">
                        <label class="font-weight-bold d-block mb-1">Idioma:</label>
                        <p class="mb-0">
                          {{ getLanguageLabel(newTemplate.language) }}
                        </p>
                      </div>
                    </b-col>
                  </b-row>

                  <template v-if="newTemplate.tipo === 'customizado' && newTemplate.layout === 'carousel'">
                    <div class="review-item mb-3">
                      <label class="font-weight-bold d-block mb-1">Layout:</label>
                      <p class="mb-0">
                        <b-badge variant="info">
                          Carrossel
                        </b-badge> {{ newTemplate.carouselCards.length }} cards
                      </p>
                    </div>
                    <div class="review-item mb-3">
                      <label class="font-weight-bold d-block mb-1">Corpo:</label>
                      <div class="bg-light p-2 rounded">
                        <small>{{ newTemplate.body || '(não definido)' }}</small>
                      </div>
                    </div>
                    <div class="review-item mb-3">
                      <label class="font-weight-bold d-block mb-1">Botão dos cards:</label>
                      <p class="mb-0">
                        "{{ newTemplate.carouselButtonText }}" — URL base e sufixo extraídos dos links de cada card
                      </p>
                    </div>
                    <div class="review-item mb-3">
                      <label class="font-weight-bold d-block mb-1">Links dos produtos (por card):</label>
                      <ul class="mb-0 pl-3">
                        <li
                          v-for="(card, idx) in newTemplate.carouselCards"
                          :key="idx"
                        >
                          Card {{ idx + 1 }}: <code class="small">{{ card.productLink || '(vazio)' }}</code>
                        </li>
                      </ul>
                    </div>
                  </template>
                  <template v-else>
                    <div class="review-item mb-3">
                      <label class="font-weight-bold d-block mb-1">Header:</label>
                      <p class="mb-0">
                        {{ getHeaderTypeLabel(newTemplate.headerType) }}
                      </p>
                    </div>

                    <div class="review-item mb-3">
                      <label class="font-weight-bold d-block mb-1">Corpo:</label>
                      <div class="bg-light p-2 rounded">
                        <small>{{ newTemplate.body || '(não definido)' }}</small>
                      </div>
                    </div>

                    <div class="review-item mb-3">
                      <label class="font-weight-bold d-block mb-1">Rodapé:</label>
                      <p class="mb-0">
                        {{ newTemplate.footer || '(sem rodapé)' }}
                      </p>
                    </div>

                    <div class="review-item mb-3">
                      <label class="font-weight-bold d-block mb-1">Botões:</label>
                      <p class="mb-0">
                        {{ getButtonTypeLabel(newTemplate.buttonType) }}
                      </p>
                    </div>
                  </template>

                  <b-alert
                    show
                    variant="info"
                    class="mb-0"
                  >
                    <div class="d-flex align-items-start">
                      <feather-icon
                        icon="InfoIcon"
                        size="16"
                        class="mr-2 mt-1"
                      />
                      <div>
                        <small>
                          Confira o preview ao lado antes de enviar. O template será enviado para aprovação do WhatsApp.
                        </small>
                      </div>
                    </div>
                  </b-alert>

                  <!-- Informações do Template (somente no passo Revisar) -->
                  <div class="template-info mt-4 pt-3 border-top">
                    <h6 class="font-weight-bold mb-2">
                      Informações do Template
                    </h6>
                    <b-alert
                      show
                      variant="light"
                      class="mb-0"
                    >
                      <small class="text-muted d-block">
                        <strong>Categoria:</strong> {{ getCategoryLabel(newTemplate.category) }}<br>
                        <strong>Idioma:</strong> {{ getLanguageLabel(newTemplate.language) }}<br>
                        <strong>Status:</strong> Será enviado para aprovação do WhatsApp
                      </small>
                    </b-alert>
                  </div>
                </div>
              </div>
            </div>

            <!-- Navigation Actions -->
            <div class="d-flex justify-content-between align-items-center pt-3 border-top mt-4">
              <b-button
                v-if="showPreviousButton"
                variant="outline-secondary"
                @click="previousStep"
              >
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="16"
                  class="mr-1"
                />
                Anterior
              </b-button>
              <b-button
                v-else
                variant="outline-secondary"
                @click="closeCreateModal"
              >
                <feather-icon
                  icon="XIcon"
                  size="16"
                  class="mr-1"
                />
                Cancelar
              </b-button>

              <div class="d-flex align-items-center">
                <small class="text-muted mr-3">Etapa {{ currentStepDisplay }} de {{ stepsToDisplay.length }}</small>
                <b-button
                  v-if="currentStep < steps.length"
                  variant="primary"
                  :disabled="!canProceedToNextStep"
                  @click="nextStep"
                >
                  Próxima
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="16"
                    class="ml-1"
                  />
                </b-button>
                <b-button
                  v-else
                  variant="success"
                  :disabled="criandoTemplate || uploadingMedia"
                  @click="createTemplate"
                >
                  <b-spinner
                    v-if="criandoTemplate"
                    small
                    class="mr-1"
                  />
                  <feather-icon
                    v-else
                    icon="SendIcon"
                    size="16"
                    class="mr-1"
                  />
                  {{ criandoTemplate ? 'Criando...' : 'Enviar para Aprovação' }}
                </b-button>
              </div>
            </div>
          </b-form>
        </b-col>

        <!-- Preview - Lado Direito -->
        <b-col
          lg="5"
          class="preview-section p-4 modal-preview-scrollable"
        >
          <h5 class="mb-3">
            <feather-icon
              icon="EyeIcon"
              size="18"
              class="mr-2"
            />
            Preview do Template
          </h5>

          <div class="whatsapp-phone-preview">
            <div class="phone-header">
              <div class="phone-status-bar">
                <span>9:41</span>
                <div class="phone-icons">
                  <feather-icon
                    icon="WifiIcon"
                    size="12"
                  />
                  <feather-icon
                    icon="BatteryIcon"
                    size="12"
                  />
                </div>
              </div>
              <div class="phone-chat-header">
                <b-avatar
                  size="32"
                  variant="light-success"
                  class="mr-2"
                >
                  <feather-icon
                    icon="UserIcon"
                    size="16"
                  />
                </b-avatar>
                <span class="font-weight-bold">Cliente</span>
              </div>
            </div>

            <div class="phone-messages">
              <div class="message-bubble">
                <!-- Header Preview (apenas template simples) -->
                <div
                  v-if="newTemplate.layout !== 'carousel' && newTemplate.headerType && newTemplate.headerType !== 'NONE'"
                  class="message-header"
                >
                  <div
                    v-if="newTemplate.headerType === 'TEXT'"
                    class="header-text"
                  >
                    <strong>{{ newTemplate.headerText || '[Texto do Header]' }}</strong>
                  </div>
                  <div
                    v-else-if="newTemplate.headerType === 'IMAGE'"
                    class="header-media"
                  >
                    <img
                      v-if="newTemplate.headerMediaPreview"
                      :src="newTemplate.headerMediaPreview"
                      alt="Preview da Imagem"
                      class="header-media-preview-img"
                    >
                    <div
                      v-else
                      class="media-placeholder"
                    >
                      <feather-icon
                        icon="ImageIcon"
                        size="32"
                        class="text-muted"
                      />
                      <small class="text-muted d-block mt-1">Imagem</small>
                    </div>
                  </div>
                  <div
                    v-else-if="newTemplate.headerType === 'VIDEO'"
                    class="header-media"
                  >
                    <video
                      v-if="newTemplate.headerMediaPreview"
                      :src="newTemplate.headerMediaPreview"
                      class="header-media-preview-video"
                      controls
                      muted
                    />
                    <div
                      v-else
                      class="media-placeholder"
                    >
                      <feather-icon
                        icon="VideoIcon"
                        size="32"
                        class="text-muted"
                      />
                      <small class="text-muted d-block mt-1">Vídeo</small>
                    </div>
                  </div>
                  <div
                    v-else-if="newTemplate.headerType === 'DOCUMENT'"
                    class="header-media"
                  >
                    <div class="media-placeholder">
                      <feather-icon
                        icon="FileTextIcon"
                        size="32"
                        class="text-muted"
                      />
                      <small class="text-muted d-block mt-1">Documento</small>
                    </div>
                  </div>
                </div>

                <!-- Body Preview -->
                <div class="message-body">
                  <p>{{ newTemplate.body || '[Corpo da mensagem aparecerá aqui]' }}</p>
                </div>

                <!-- No carrossel: hora dentro do bubble; depois o carrossel fica em bloco separado -->
                <template v-if="newTemplate.layout === 'carousel' && newTemplate.carouselCards && newTemplate.carouselCards.length">
                  <div class="message-time">
                    <small class="text-muted">{{ new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}</small>
                  </div>
                </template>

                <!-- Footer Preview (apenas template simples) -->
                <div
                  v-if="newTemplate.layout !== 'carousel' && newTemplate.footer"
                  class="message-footer"
                >
                  <small class="text-muted">{{ newTemplate.footer }}</small>
                </div>

                <!-- Buttons Preview (apenas template simples) -->
                <div
                  v-if="newTemplate.layout !== 'carousel' && newTemplate.buttonType !== 'NONE'"
                  class="message-buttons"
                >
                  <!-- Quick Reply -->
                  <div v-if="newTemplate.buttonType === 'QUICK_REPLY'">
                    <div
                      v-for="(button, index) in newTemplate.quickReplyButtons.filter(b => b.text)"
                      :key="index"
                      class="button-item"
                    >
                      <feather-icon
                        icon="CornerDownRightIcon"
                        size="14"
                        class="mr-1"
                      />
                      {{ button.text }}
                    </div>
                  </div>

                  <!-- Call to Action -->
                  <div v-if="newTemplate.buttonType === 'CALL_TO_ACTION'">
                    <div
                      v-if="newTemplate.urlButton.text"
                      class="button-item"
                    >
                      <feather-icon
                        icon="ExternalLinkIcon"
                        size="14"
                        class="mr-1"
                      />
                      {{ newTemplate.urlButton.text }}
                    </div>
                    <div
                      v-if="newTemplate.urlButton.url"
                      class="button-url-link-modal"
                    >
                      <small class="text-muted">{{ getUrlButtonDisplay(newTemplate.urlButton) }}</small>
                    </div>
                  </div>

                  <!-- Copiar chave Pix (ORDER_DETAILS) -->
                  <div
                    v-if="newTemplate.buttonType === 'ORDER_DETAILS'"
                    class="button-item"
                  >
                    <feather-icon
                      icon="CopyIcon"
                      size="14"
                      class="mr-1"
                    />
                    Copiar chave Pix
                  </div>
                </div>

                <div
                  v-if="newTemplate.layout !== 'carousel'"
                  class="message-time"
                >
                  <small class="text-muted">{{ new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}</small>
                </div>
              </div>

              <!-- Carrossel em bloco separado (como campaign/inteligentes/recomendacao) -->
              <div
                v-if="newTemplate.layout === 'carousel' && newTemplate.carouselCards && newTemplate.carouselCards.length"
                class="carousel-preview-standalone"
              >
                <div class="carousel-scroll carousel-scroll-one-and-quarter">
                  <div
                    v-for="(card, idx) in newTemplate.carouselCards"
                    :key="idx"
                    class="carousel-item-preview"
                  >
                    <div class="carousel-image">
                      <img
                        v-if="card.imagePreview"
                        :src="card.imagePreview"
                        alt="Produto"
                        class="carousel-image-img"
                      >
                      <feather-icon
                        v-else
                        icon="ImageIcon"
                        size="48"
                        class="text-muted"
                      />
                    </div>
                    <div class="carousel-info">
                      <p class="product-name mb-1">
                        Produto #{{ idx + 1 }}
                      </p>
                      <div class="carousel-buttons">
                        <a
                          v-if="card.productLink"
                          :href="card.productLink"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="carousel-btn carousel-btn-link"
                        >
                          <feather-icon
                            icon="ExternalLinkIcon"
                            size="12"
                            class="mr-1"
                          />
                          {{ newTemplate.carouselButtonText || 'Acessar' }}
                        </a>
                        <span
                          v-else
                          class="carousel-btn"
                        >
                          <feather-icon
                            icon="ExternalLinkIcon"
                            size="12"
                            class="mr-1"
                          />
                          {{ newTemplate.carouselButtonText || 'Acessar' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="text-center mt-2">
                  <small class="text-muted">← Deslize para ver mais →</small>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-modal>

    <!-- Modal de Visualização -->
    <b-modal
      v-model="showViewModal"
      :title="`Template: ${templateSelecionado ? templateSelecionado.name : ''}`"
      size="lg"
      hide-footer
      centered
    >
      <div v-if="templateSelecionado">
        <!-- Metadados -->
        <div class="view-meta mb-3">
          <b-row>
            <b-col
              cols="6"
              class="mb-2"
            >
              <small class="text-muted d-block">Status</small>
              <b-badge
                :variant="getStatusVariant(templateSelecionado.status)"
                class="mt-1"
              >
                <feather-icon
                  :icon="getStatusIcon(templateSelecionado.status)"
                  size="14"
                  class="mr-50"
                />
                {{ getStatusText(templateSelecionado.status) }}
              </b-badge>
            </b-col>
            <b-col
              cols="6"
              class="mb-2"
            >
              <small class="text-muted d-block">Idioma</small>
              <b-badge
                variant="light-secondary"
                class="mt-1"
              >
                <feather-icon
                  icon="GlobeIcon"
                  size="14"
                  class="mr-50"
                />
                {{ getLanguageText(templateSelecionado.language) }}
              </b-badge>
            </b-col>
            <b-col cols="6">
              <small class="text-muted d-block">Categoria</small>
              <b-badge
                variant="light-info"
                class="mt-1"
              >
                <feather-icon
                  icon="TagIcon"
                  size="14"
                  class="mr-50"
                />
                {{ templateSelecionado.category }}
              </b-badge>
            </b-col>
            <b-col cols="6">
              <small class="text-muted d-block">ID</small>
              <code class="mt-1 d-block">{{ templateSelecionado.id }}</code>
            </b-col>
          </b-row>
        </div>

        <!-- Preview -->
        <div class="view-content">
          <h6 class="mb-2">
            <feather-icon
              icon="MessageSquareIcon"
              size="16"
              class="mr-1"
            />
            Preview da Mensagem
          </h6>
          <div class="whatsapp-preview-full">
            <!-- Preview para Carrossel -->
            <template v-if="templateSelecionado.layout === 'carousel' && templateSelecionado.carouselCards && templateSelecionado.carouselCards.length">
              <div class="whatsapp-message-container">
                <!-- Bubble com Body -->
                <div class="whatsapp-bubble mb-3">
                  <p class="whatsapp-body-text">
                    {{ templateSelecionado.content || templateSelecionado.body }}
                  </p>
                  <div class="whatsapp-timestamp">
                    {{ new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}
                  </div>
                </div>

                <!-- Carrossel de Cards -->
                <div class="carousel-modal-preview">
                  <div class="carousel-scroll-modal">
                    <div
                      v-for="(card, idx) in templateSelecionado.carouselCards"
                      :key="idx"
                      class="carousel-card-modal"
                    >
                      <div class="carousel-card-image-modal">
                        <img
                          v-if="card.imagePreview || card.imageUrl"
                          :src="card.imagePreview || card.imageUrl"
                          alt="Produto"
                        >
                        <div
                          v-else
                          class="carousel-card-placeholder-modal"
                        >
                          <feather-icon
                            icon="ImageIcon"
                            size="48"
                          />
                        </div>
                      </div>
                      <div class="carousel-card-info-modal">
                        <a
                          v-if="card.buttonUrl"
                          :href="card.buttonUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="carousel-card-button-modal"
                        >
                          <feather-icon
                            icon="ExternalLinkIcon"
                            size="14"
                            class="mr-1"
                          />
                          {{ card.buttonText || 'Ver produto' }}
                        </a>
                        <span
                          v-else
                          class="carousel-card-button-modal disabled"
                        >
                          <feather-icon
                            icon="ExternalLinkIcon"
                            size="14"
                            class="mr-1"
                          />
                          {{ card.buttonText || 'Ver produto' }}
                        </span>
                        <div
                          v-if="card.buttonUrl"
                          class="carousel-card-url-display"
                        >
                          <small class="text-muted">{{ card.buttonUrl }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="text-center mt-2">
                    <small class="text-muted">← Deslize para ver todos os {{ templateSelecionado.carouselCards.length }} produtos →</small>
                  </div>
                </div>
              </div>
            </template>

            <!-- Preview Normal (não carrossel) -->
            <div
              v-else
              class="whatsapp-message-container"
            >
              <!-- Header com Imagem/Vídeo (fora da bubble) -->
              <div
                v-if="(templateSelecionado.headerType === 'IMAGE' || templateSelecionado.headerType === 'VIDEO') && templateSelecionado.headerMediaUrl"
                class="whatsapp-media-header"
              >
                <img
                  v-if="templateSelecionado.headerType === 'IMAGE'"
                  :src="templateSelecionado.headerMediaUrl"
                  alt="Header Image"
                  class="whatsapp-media-image"
                >
                <video
                  v-else-if="templateSelecionado.headerType === 'VIDEO'"
                  :src="templateSelecionado.headerMediaUrl"
                  class="whatsapp-media-video"
                  controls
                />
              </div>

              <!-- Bubble do WhatsApp -->
              <div class="whatsapp-bubble">
                <!-- Header Text (dentro da bubble) -->
                <strong
                  v-if="templateSelecionado.headerType === 'TEXT'"
                  class="whatsapp-header-text"
                >
                  {{ templateSelecionado.headerText }}
                </strong>

                <!-- Body -->
                <p class="whatsapp-body-text">
                  {{ templateSelecionado.content }}
                </p>

                <!-- Footer -->
                <small
                  v-if="templateSelecionado.footer"
                  class="whatsapp-footer-text"
                >
                  {{ templateSelecionado.footer }}
                </small>

                <!-- Timestamp -->
                <div class="whatsapp-timestamp">
                  {{ new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}
                </div>
              </div>

              <!-- Buttons (fora da bubble) -->
              <div
                v-if="templateSelecionado.buttonType && templateSelecionado.buttonType !== 'NONE'"
                class="whatsapp-buttons"
              >
                <!-- Quick Reply Buttons -->
                <template v-if="templateSelecionado.buttonType === 'QUICK_REPLY'">
                  <button
                    v-for="(button, index) in templateSelecionado.quickReplyButtons.filter(b => b.text)"
                    :key="index"
                    class="whatsapp-button whatsapp-button-reply"
                  >
                    <feather-icon
                      icon="CornerDownRightIcon"
                      size="16"
                    />
                    {{ button.text }}
                  </button>
                </template>

                <!-- Call to Action - URL Button -->
                <div
                  v-if="templateSelecionado.buttonType === 'CALL_TO_ACTION' && templateSelecionado.urlButton && templateSelecionado.urlButton.text"
                  class="url-button-container"
                >
                  <button
                    class="whatsapp-button whatsapp-button-url whatsapp-button-animated"
                    @click="copyUrlToClipboard"
                  >
                    <feather-icon
                      icon="ExternalLinkIcon"
                      size="16"
                      color="#992F92"
                    />
                    <a
                      class="button-text-wrapper"
                      :href="templateSelecionado.urlButton.url"
                      target="_blank"
                    >
                      <span class="button-text-wrapper">
                        <span class="button-text-original">{{ templateSelecionado.urlButton.text }}</span>
                        <span class="button-text-hover">Clique para acessar</span>
                      </span>
                    </a>
                  </button>
                  <div
                    v-if="templateSelecionado.urlButton.url"
                    class="button-url-display"
                  >
                    <small class="text-muted">{{ urlButtonDisplay }}</small>
                  </div>
                  <transition name="fade">
                    <div
                      v-if="showCopiedTooltip"
                      class="copied-tooltip"
                    >
                      Link copiado
                    </div>
                  </transition>
                </div>

                <!-- Call to Action - Phone Button -->
                <button
                  v-if="templateSelecionado.buttonType === 'CALL_TO_ACTION' && templateSelecionado.phoneButton && templateSelecionado.phoneButton.text"
                  class="whatsapp-button whatsapp-button-phone"
                >
                  <feather-icon
                    icon="PhoneIcon"
                    size="16"
                  />
                  {{ templateSelecionado.phoneButton.text }}
                </button>

                <!-- Copiar chave Pix (ORDER_DETAILS) -->
                <button
                  v-if="templateSelecionado.buttonType === 'ORDER_DETAILS'"
                  class="whatsapp-button whatsapp-button-reply"
                >
                  <feather-icon
                    icon="CopyIcon"
                    size="16"
                  />
                  Copiar chave Pix
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Ações -->
        <div class="d-flex justify-content-end mt-3">
          <b-button
            variant="outline-primary"
            @click="showViewModal = false"
          >
            <feather-icon
              icon="XIcon"
              size="16"
              class="mr-1"
            />
            Fechar
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal de Políticas do WhatsApp -->
    <b-modal
      v-model="showPoliciesModal"
      title="Políticas do WhatsApp para Templates"
      size="lg"
      centered
      scrollable
      ok-only
      ok-title="Entendi"
      ok-variant="primary"
      body-class="policies-modal-body"
    >
      <div class="policies-content">
        <!-- Introdução -->
        <b-alert
          show
          variant="info"
          class="mb-2"
        >
          <div class="d-flex align-items-start">
            <feather-icon
              icon="AlertCircleIcon"
              size="18"
              class="mr-2 mt-1 flex-shrink-0"
            />
            <div>
              <strong class="d-block mb-1">📋 Políticas Oficiais do WhatsApp Business API</strong>
              <small class="mb-0 d-block">
                Todos os templates passam por <strong>revisão e aprovação</strong> (pode levar até 24 horas).
                Violações podem resultar em <strong>rejeição, suspensão ou banimento permanente</strong>.
              </small>
            </div>
          </div>
        </b-alert>

        <!-- Categorias de Templates -->
        <div class="policy-section mb-2">
          <h6 class="policy-title">
            <feather-icon
              icon="LayersIcon"
              size="16"
              class="text-info mr-2"
            />
            📂 Categorias de Templates
          </h6>
          <div class="mb-2">
            <strong class="d-block mb-1">🔹 UTILITY (Utilidade)</strong>
            <small class="text-muted">
              Mensagens transacionais relacionadas a ações específicas do usuário: confirmações de pedidos,
              atualizações de status, notificações de entrega, alertas de conta.
            </small>
          </div>
          <div>
            <strong class="d-block mb-1">🔹 MARKETING</strong>
            <small class="text-muted">
              Mensagens promocionais ou ofertas: lançamentos de produtos, promoções (REQUER OPT-IN explícito).
            </small>
          </div>
        </div>

        <!-- Consentimento do Usuário -->
        <div class="policy-section mb-2">
          <h6 class="policy-title">
            <feather-icon
              icon="UserCheckIcon"
              size="16"
              class="text-success mr-2"
            />
            ✅ Consentimento do Usuário (OPT-IN)
          </h6>
          <p class="mb-2">
            <strong>É OBRIGATÓRIO obter consentimento explícito antes de enviar mensagens.</strong>
          </p>
          <ul class="policy-list mb-2">
            <li><strong>Templates de Utilidade:</strong> Geralmente não exigem opt-in se forem transacionais</li>
            <li><strong>Templates de Marketing:</strong> SEMPRE exigem opt-in claro e explícito</li>
          </ul>
          <b-alert
            show
            variant="warning"
            class="mb-0 mt-2"
          >
            <small>
              <strong>⚠️ Importante:</strong> O opt-in deve indicar claramente que o usuário concorda em receber
              mensagens via WhatsApp da sua empresa. Mensagens não solicitadas são consideradas SPAM.
            </small>
          </b-alert>
        </div>

        <!-- Diretrizes de Conteúdo -->
        <div class="policy-section mb-2">
          <h6 class="policy-title">
            <feather-icon
              icon="CheckCircleIcon"
              size="16"
              class="text-success mr-2"
            />
            ✅ Diretrizes de Conteúdo
          </h6>
          <ul class="policy-list">
            <li><strong>Clareza e Precisão:</strong> Evite erros gramaticais e ortográficos</li>
            <li><strong>Relevância:</strong> Conteúdo deve ser pertinente ao destinatário</li>
            <li><strong>Personalização:</strong> Use variáveis corretamente (ex: <code v-text="'{{1}}'" />, <code v-text="'{{2}}'" />)</li>
            <li><strong>Links:</strong> Evite encurtadores de URL (bit.ly, tinyurl). Use links diretos</li>
            <li><strong>Nomeação:</strong> Use nomes descritivos (ex: <code>confirmacao_pedido_123</code>)</li>
            <li><strong>Concisão:</strong> Mensagens diretas e fáceis de entender</li>
          </ul>
        </div>

        <!-- Proibições -->
        <div class="policy-section mb-2">
          <h6 class="policy-title">
            <feather-icon
              icon="XCircleIcon"
              size="16"
              class="text-danger mr-2"
            />
            ❌ Conteúdo PROIBIDO
          </h6>
          <ul class="policy-list">
            <li><strong>Produtos/Serviços Proibidos:</strong> Drogas, armas, tabaco, jogos de azar, criptomoedas não regulamentadas</li>
            <li><strong>Informações Sensíveis:</strong> Não solicite dados financeiros, senhas ou informações confidenciais</li>
            <li><strong>Marketing sem Opt-in:</strong> Proibido enviar promoções sem consentimento explícito</li>
            <li><strong>Conteúdo Enganoso:</strong> Informações falsas ou clickbait</li>
            <li><strong>Spam:</strong> Mensagens repetitivas ou não solicitadas</li>
            <li><strong>Linguagem Abusiva:</strong> Conteúdo ofensivo, discriminatório ou ameaçador</li>
          </ul>
        </div>

        <!-- Linguagem de SPAM -->
        <div class="policy-section mb-2">
          <h6 class="policy-title">
            <feather-icon
              icon="AlertTriangleIcon"
              size="16"
              class="text-warning mr-2"
            />
            ⚠️ Linguagem de SPAM - Evite Rejeição
          </h6>
          <p class="mb-2">
            <strong>O WhatsApp rejeita templates com características de SPAM:</strong>
          </p>
          <ul class="policy-list">
            <li><strong>MAIÚSCULAS EXCESSIVAS:</strong> Não use CAPS LOCK desnecessariamente (ex: "COMPRE AGORA!!!")</li>
            <li><strong>Múltiplos pontos de exclamação:</strong> Evite !!!! ou ??? repetidos</li>
            <li><strong>Emojis excessivos:</strong> Use com moderação (ex: 🎉🎉🎉🎉 é considerado spam)</li>
            <li><strong>Linguagem promocional agressiva:</strong> Evite tom excessivamente promocional ou urgência forçada</li>
            <li><strong>Promessas irreais:</strong> Evite declarações exageradas ou garantias impossíveis</li>
            <li><strong>Clickbait:</strong> Não use táticas de isca de cliques ou informações enganosas</li>
          </ul>
          <b-alert
            show
            variant="warning"
            class="mb-0 mt-2"
          >
            <small>
              <strong>💡 Dica:</strong> Use tom profissional e informativo. Templates de <strong>MARKETING</strong>
              devem ser claros sobre sua natureza promocional e sempre ter <strong>opt-in explícito</strong> do usuário.
            </small>
          </b-alert>
        </div>

        <!-- ERRO COMUM: Mensagens Aparentemente Inofensivas -->
        <div
          class="policy-section mb-2"
          style="border-left-color: #ea5455;"
        >
          <h6 class="policy-title">
            <feather-icon
              icon="AlertOctagonIcon"
              size="16"
              class="text-danger mr-2"
            />
            🚨 ERRO COMUM: Por que mensagens "simples" causam BANIMENTO
          </h6>
          <b-alert
            show
            variant="danger"
            class="mb-2"
          >
            <strong>Exemplo de mensagem que causou banimento:</strong>
            <div
              class="mt-2 p-2"
              style="background: #fff; border-radius: 4px; font-style: italic; color: #000;"
            >
              "Olá, aqui é da Loja X, você pode comprar pelo nosso aplicativo"
            </div>
          </b-alert>

          <p class="mb-1">
            <strong>❌ Por que essa mensagem causou banimento?</strong>
          </p>
          <ul class="policy-list mb-2">
            <li><strong>NÃO foi o texto em si</strong> (texto é simples, sem spam)</li>
            <li><strong>FOI o uso indevido:</strong> Enviada para pessoas <u>sem opt-in explícito</u></li>
            <li><strong>Categoria errada:</strong> É marketing, mas foi enviada como utilidade</li>
            <li><strong>Mensagens em massa:</strong> Enviada para muitos contatos não solicitados</li>
            <li><strong>Múltiplas reclamações:</strong> Usuários bloquearam/reportaram como spam</li>
          </ul>

          <div class="comparison-box mb-2">
            <div class="comparison-wrong">
              <strong class="d-block mb-1">❌ ERRADO (Causa Banimento)</strong>
              <small>
                • Enviar para lista antiga de clientes<br>
                • Sem opt-in específico para WhatsApp<br>
                • Usar template UTILITY para marketing<br>
                • Enviar "convite para comprar" sem contexto
              </small>
            </div>
            <div class="comparison-right">
              <strong class="d-block mb-1">✅ CORRETO</strong>
              <small>
                • Apenas para quem deu opt-in explícito<br>
                • Categoria MARKETING correta<br>
                • Relacionado a ação específica do cliente<br>
                • Contexto transacional (pedido, entrega)
              </small>
            </div>
          </div>

          <b-alert
            show
            variant="warning"
            class="mb-0"
          >
            <small>
              <strong>⚠️ Regra de Ouro:</strong> Mesmo mensagens "inocentes" causam banimento se enviadas
              <strong>sem permissão explícita</strong>. O problema NÃO é o texto, é o <strong>envio não solicitado</strong>.
            </small>
          </b-alert>
        </div>

        <!-- Qualidade e Reputação -->
        <div class="policy-section mb-2">
          <h6 class="policy-title">
            <feather-icon
              icon="TrendingUpIcon"
              size="16"
              class="text-info mr-2"
            />
            📊 Qualidade e Reputação da Conta
          </h6>
          <p class="mb-2">
            <strong>O WhatsApp monitora continuamente a qualidade dos seus templates:</strong>
          </p>
          <ul class="policy-list">
            <li><strong>Feedback dos usuários:</strong> Bloqueios e reclamações afetam sua reputação</li>
            <li><strong>Taxa de resposta:</strong> Mensagens irrelevantes reduzem o engajamento</li>
            <li><strong>Classificação de qualidade:</strong> Verde (boa), Amarelo (média), Vermelho (baixa)</li>
            <li><strong>Desativação automática:</strong> Templates com baixa qualidade são pausados</li>
          </ul>
        </div>

        <!-- Boas Práticas -->
        <div class="policy-section mb-2">
          <h6 class="policy-title">
            <feather-icon
              icon="ThumbsUpIcon"
              size="16"
              class="text-primary mr-2"
            />
            💡 Boas Práticas para Aprovação
          </h6>
          <ul class="policy-list">
            <li><strong>Categorização correta:</strong> Escolha a categoria apropriada (Utility, Auth, Marketing)</li>
            <li><strong>Teste antes de aprovar:</strong> Revise gramática, ortografia e formatação</li>
            <li><strong>Use botões interativos:</strong> "Rastrear Pedido", "Confirmar" ao invés de links simples</li>
            <li><strong>Personalize adequadamente:</strong> Variáveis devem fazer sentido no contexto</li>
            <li><strong>Forneça valor real:</strong> Mensagem deve ser útil e relevante para o destinatário</li>
            <li><strong>Evite duplicatas:</strong> Não crie templates similares com nomes diferentes</li>
            <li><strong>Monitore métricas:</strong> Acompanhe taxa de entrega, leitura e bloqueios</li>
          </ul>
        </div>

        <!-- Processo de Aprovação -->
        <div class="policy-section mb-2">
          <h6 class="policy-title">
            <feather-icon
              icon="ClockIcon"
              size="16"
              class="text-success mr-2"
            />
            ⏱️ Processo de Aprovação
          </h6>
          <ul class="policy-list">
            <li><strong>Tempo de aprovação:</strong> Pode levar até 24 horas</li>
            <li><strong>Aprovação rápida:</strong> Templates simples podem ser aprovados em minutos</li>
            <li><strong>Status:</strong> Pendente → Aprovado ou Rejeitado</li>
            <li><strong>Reenvio:</strong> Templates rejeitados podem ser corrigidos e reenviados</li>
          </ul>
        </div>

        <!-- Consequências -->
        <b-alert
          show
          variant="danger"
          class="mb-0"
        >
          <div class="d-flex align-items-start">
            <feather-icon
              icon="AlertOctagonIcon"
              size="20"
              class="mr-2 mt-1 flex-shrink-0"
            />
            <div>
              <strong class="d-block mb-2">⛔ Consequências de Violações</strong>
              <div class="mb-2">
                <strong class="text-danger">Violações Leves:</strong>
                <ul class="mb-1 pl-3">
                  <li>Rejeição do template</li>
                  <li>Necessidade de correção e reenvio</li>
                </ul>
              </div>
              <div class="mb-2">
                <strong class="text-danger">Violações Graves ou Repetidas:</strong>
                <ul class="mb-1 pl-3">
                  <li>Suspensão temporária da conta (7-30 dias)</li>
                  <li>Redução do limite de mensagens</li>
                  <li>Revisão manual obrigatória de futuros templates</li>
                </ul>
              </div>
              <div>
                <strong class="text-danger">Violações Muito Graves:</strong>
                <ul class="mb-0 pl-3">
                  <li>Banimento permanente da conta comercial</li>
                  <li>Perda total de acesso à API do WhatsApp Business</li>
                  <li>Impossibilidade de criar nova conta (blacklist)</li>
                </ul>
              </div>
            </div>
          </div>
        </b-alert>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BButton, BModal, BForm, BFormInput, BFormTextarea,
  BFormSelect, BFormGroup, BRow, BCol, BBadge, BSpinner,
  BDropdown, BDropdownItem, BDropdownDivider, BPagination,
  BFormSelectOption, BAlert, BInputGroup, BInputGroupAppend, BAvatar,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import {
  listarTemplates, excluirTemplate, criarTemplate, buscaChatsTeste, uploadTemplateMedia,
} from '@/functions/basics/chat'

export default {
  name: 'TemplatesWhatsApp',
  components: {
    BCard,
    BButton,
    BModal,
    BForm,
    BFormInput,
    BFormTextarea,
    BFormSelect,
    BFormGroup,
    BRow,
    BCol,
    BBadge,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BPagination,
    BFormSelectOption,
    BAlert,
    BInputGroup,
    BInputGroupAppend,
    BAvatar,
    FeatherIcon,
  },
  data() {
    return {
      facebookAccountsLista: [],
      chatSelecionado: null,
      totalFiltered: 0,
      currentPage: 1,
      perPage: 9,
      carregando: false,
      showCreateModal: false,
      showViewModal: false,
      showPoliciesModal: false,
      filterStatus: null,
      filterCategory: null,
      searchQuery: '',
      avisoCarrosselSemVariaveis: 'O corpo do template de carrossel deve conter apenas texto fixo. Não use placeholders como {{primeiro_nome}}.',
      templates: [],
      templateSelecionado: null,
      // Wizard Step-by-Step
      currentStep: 1,
      currentSubStep: 1, // Para controlar sub-steps
      steps: [
        { label: 'Utilidade/Tipo', icon: 'TargetIcon' },
        { label: 'Início', icon: 'InfoIcon' },
        { label: 'Conteúdo', icon: 'FileTextIcon' },
        { label: 'Botões', icon: 'MousePointerIcon' },
        { label: 'Revisar', icon: 'CheckCircleIcon' },
      ],
      setupSubSteps: [
        { label: 'Nome', icon: 'EditIcon' },
        { label: 'Utilidade', icon: 'TargetIcon' },
        { label: 'Tipo', icon: 'LayersIcon' },
      ],
      // Opções de utilidade do template
      templateUtilidades: [
        {
          value: 'retorno',
          label: 'Template de Retorno',
          description: 'Para retornar o chat com o cliente na Central de Atendimento',
          icon: 'MessageCircleIcon',
          color: 'primary',
        },
        {
          value: 'notificacao',
          label: 'Template de Notificações',
          description: 'Para enviar notificações automáticas (pedido aprovado, rastreio, etc)',
          icon: 'BellIcon',
          color: 'warning',
        },
        {
          value: 'campanha',
          label: 'Template de Campanhas',
          description: 'Para usar em campanhas de marketing e promoções',
          icon: 'SendIcon',
          color: 'success',
        },
      ],
      // Opções de tipo do template
      templateTipos: [
        {
          value: 'predefinido',
          label: 'Template Pré-definido',
          description: 'Escolha um modelo pronto e personalize conforme sua necessidade. Maior probabilidade de ser aprovado como MARKETING',
          icon: 'PackageIcon',
          color: 'info',
        },
        {
          value: 'customizado',
          label: 'Template Customizado',
          description: 'Crie um template do zero com total liberdade',
          icon: 'Edit3Icon',
          color: 'secondary',
        },
      ],
      newTemplate: {
        utilidade: null, // 'retorno', 'notificacao', 'campanha'
        tipo: null, // 'predefinido', 'customizado'
        layout: 'single', // 'single' | 'carousel' (apenas para customizado)
        name: '',
        language: 'pt_BR',
        category: 'UTILITY',
        headerType: 'NONE',
        headerText: '',
        headerMedia: null,
        headerMediaPreview: null,
        body: '',
        footer: '',
        buttonType: 'NONE',
        quickReplyButtons: [{ text: '' }],
        urlButton: { text: '', url: '', type: 'static' },
        phoneButton: { text: '', phone: '' },
        orderDetailsButton: { text: 'Copiar chave Pix' },
        // Carrossel (2 a 10 cards) — cada card: imagem + link completo do produto
        carouselCards: [
          { image: null, imagePreview: null, productLink: '' },
          { image: null, imagePreview: null, productLink: '' },
        ],
        carouselButtonText: '',
      },
      _utmCampaignId: '',
      uploadingMedia: false,
      criandoTemplate: false,
      showCopiedTooltip: false,
      filtroCategoriaPredefinida: null, // Filtro de categoria para templates pré-definidos
      availableVariables: [
        { key: '{{primeiro_nome}}', label: 'Primeiro Nome', description: 'Nome do cliente' },
        { key: '{{email}}', label: 'Email', description: 'Email do cliente' },
        { key: '{{numero_pedido}}', label: 'Nº Pedido', description: 'Número do pedido' },
        { key: '{{valor_total}}', label: 'Valor Total', description: 'Valor total do pedido' },
        { key: '{{link_do_boleto}}', label: 'Link Boleto', description: 'URL completa do boleto' },
        { key: '{{codigo_do_boleto}}', label: 'Cód. Boleto', description: 'Código do boleto' },
        { key: '{{link_de_rastreio}}', label: 'Link de Rastreio', description: 'URL completa de rastreamento' },
        { key: '{{codigo_de_rastreio}}', label: 'Cód. Rastreio', description: 'Código de rastreio' },
        { key: '{{link_de_avaliacao}}', label: 'Link de Avaliação', description: 'URL completa de avaliação' },
        { key: '{{link_do_carrinho}}', label: 'Link do Carrinho', description: 'URL completa do carrinho' },
        { key: '{{data_entrega}}', label: 'Data Entrega', description: 'Data de entrega' },
        { key: '{{total_pedido}}', label: 'Total Pedido', description: 'Valor total do pedido' },
        { key: '{{saldo}}', label: 'Saldo', description: 'Saldo disponível' },
        { key: '{{data_expiracao}}', label: 'Data Expiração', description: 'Data de expiração' },
      ],
      urlVariables: [
        { key: '{{link_de_rastreio}}', label: 'Link de Rastreio' },
        { key: '{{link_de_avaliacao}}', label: 'Link de Avaliação' },
        { key: '{{link_do_boleto}}', label: 'Link do Boleto' },
        { key: '{{link_do_carrinho}}', label: 'Link do Carrinho' },
      ],
      predefinedTemplates: [
        {
          name: 'retorno_atendimento',
          category: 'UTILITY',
          utilidade: 'retorno',
          headerType: 'NONE',
          body: 'Olá! Aqui é do atendimento da NOME DA LOJA\n\nTemos um retorno sobre sua solicitação. Quando puder, responda a esta mensagem para darmos continuidade ao seu atendimento',
          footer: '',
          buttonType: 'NONE',
          description: 'Template para retomar atendimento com o cliente',
        },
        {
          name: 'pedido_em_transporte',
          category: 'UTILITY',
          headerType: 'NONE',
          body: 'Olá {{primeiro_nome}}! 📦\n\nSeu pedido {{numero_pedido}} já está a caminho!\n\nAcompanhe seu pedido em tempo real.',
          footer: '',
          buttonType: 'CALL_TO_ACTION',
          urlButton: { text: 'Acompanhar Pedido', url: '{{link_de_rastreio}}', type: 'dynamic' },
          description: 'Notificação de pedido em transporte com rastreio',
        },
        {
          name: 'pedido_aprovado',
          category: 'UTILITY',
          headerType: 'TEXT',
          headerText: 'Pedido Aprovado!',
          body: 'Olá {{primeiro_nome}}!\n\nSeu pedido #{{numero_pedido}} foi aprovado com sucesso!\n\nValor: {{total_pedido}}\nObrigado pela preferência!',
          footer: 'Equipe de Vendas',
          buttonType: 'NONE',
          description: 'Confirmação de aprovação de pedido',
        },
        {
          name: 'pedido_entregue',
          category: 'UTILITY',
          headerType: 'TEXT',
          headerText: 'Pedido Entregue!',
          body: 'Olá {{primeiro_nome}}!\n\nSeu pedido #{{numero_pedido}} foi entregue com sucesso!\n\nEsperamos que você goste! 😊\n\nQue tal avaliar sua experiência?',
          footer: 'Obrigado por comprar conosco!',
          buttonType: 'CALL_TO_ACTION',
          urlButton: { text: 'Avaliar Pedido', url: '{{link_de_avaliacao}}', type: 'dynamic' },
          description: 'Confirmação de entrega com pedido de avaliação',
        },
        {
          name: 'pedido_cancelado',
          category: 'UTILITY',
          headerType: 'TEXT',
          headerText: 'Pedido Cancelado!',
          body: 'Olá {{primeiro_nome}},\n\nInfelizmente seu pedido #{{numero_pedido}} foi cancelado.\n\nEstamos à disposição para ajudar!',
          footer: 'Atendimento ao Cliente',
          buttonType: 'QUICK_REPLY',
          quickReplyButtons: [{ text: 'Falar com Suporte' }],
          description: 'Notificação de cancelamento de pedido',
        },
        {
          name: 'carrinho_abandonado',
          category: 'UTILITY',
          headerType: 'TEXT',
          headerText: 'Esqueceu algo?',
          body: 'Olá {{primeiro_nome}}!\n\nVimos que você iniciou um pedido em nosso site, mas ele não foi finalizado.\n\nCaso tenha ocorrido algum problema durante a compra, você pode continuar de onde parou através do link abaixo:',
          footer: 'Se já concluiu a compra, desconsidere esta mensagem.',
          buttonType: 'CALL_TO_ACTION',
          urlButton: { text: 'Ver Pedido', url: '{{link_do_carrinho}}', type: 'dynamic' },
          description: 'Lembrete de carrinho abandonado',
        },
        {
          name: 'cashback_disponivel',
          category: 'UTILITY',
          headerType: 'TEXT',
          headerText: 'Cashback Disponível!',
          body: 'Olá {{primeiro_nome}}!\n\nSeu cashback exclusivo está ativo ✨\n\nUse já e garanta seu desconto!',
          footer: 'Aproveite antes que expire',

          description: 'Notificação de cashback disponível',
        },
        {
          name: 'pedido_pix',
          category: 'UTILITY',
          headerType: 'NONE',
          body: 'Olá {{primeiro_nome}}, receebemos o seu pedido {{numero_pedido}} no valor de R$ {{valor_total}}.\nO status do pedido é: pagamento pendente. \n\nAbaixo a chave pix do seu pedido, basta copiar e colar no seu banco.',
          footer: 'Se você já pagou, desconsidere essa mensagem.',
          buttonType: 'ORDER_DETAILS',
          orderDetailsButton: { text: 'Copiar chave Pix' },
          description: 'Aguardando pagamento PIX com botão para copiar chave',
        },
        {
          name: 'campanha_video',
          category: 'MARKETING',
          headerType: 'VIDEO',
          body: 'Olá {{primeiro_nome}}! 🎬\n\nConfira nossa novidade!\n\nNão perca essa oportunidade de conhecer nossos produtos.',
          footer: 'Acesse agora',
          buttonType: 'CALL_TO_ACTION',
          urlButton: { text: 'Saiba Mais', url: 'https://sualoja.com/campanha', type: 'static' },
          description: 'Campanha de marketing com vídeo e link',
        },
        {
          name: 'campanha_imagem',
          category: 'MARKETING',
          headerType: 'IMAGE',
          body: 'Olá {{primeiro_nome}}! 📸\n\nVeja nossas novidades!\n\nClique no botão abaixo para descobrir mais.',
          footer: 'Não perca!',
          buttonType: 'CALL_TO_ACTION',
          urlButton: { text: 'Ver Ofertas', url: 'https://sualoja.com/ofertas', type: 'static' },
          description: 'Campanha de marketing com imagem e link',
        },
        {
          name: 'carousel_recomendacao_produtos',
          category: 'MARKETING',
          layout: 'carousel',
          body: 'Olá {{primeiro_nome}}! \n\nSeparei umas peças que são a sua cara. Confira aí, escolha teus favoritos e já garante!\n\nCupom *PROMO15* ✨ 15% OFF.',
          description: 'Carrossel de recomendação de produtos (2 a 10 cards com imagem e link por produto)',
        },
        {
          name: 'recomendacao_produtos',
          category: 'MARKETING',
          layout: 'carousel',
          body: 'Salve {{primeiro_nome}}! \n\nSeparei umas peças que são a sua cara. Os produtos abaixo são exemplos para aprovação na Meta — na campanha, os itens enviados mudam de acordo com o perfil de cada cliente.\n\nConfira e já garante! ✨',
          description: 'Recomendação de produtos personalizada por cliente (carrossel; produtos reais variam por perfil)',
        },
      ],
      statusOptions: [
        { value: null, text: 'Todos os Status' },
        { value: 'PENDING', text: 'Em Aprovação' },
        { value: 'APPROVED', text: 'Aprovado' },
        { value: 'REJECTED', text: 'Rejeitado' },
      ],
      categoryOptions: [
        { value: 'UTILITY', text: 'Utilidade (notificações, atualizações)' },
        { value: 'MARKETING', text: 'Marketing (promoções, ofertas)' },
      ],
      categoryFilterOptions: [
        { value: null, text: 'Todas as Categorias' },
        { value: 'UTILITY', text: 'Utilidade' },
        { value: 'MARKETING', text: 'Marketing' },
      ],
      languageOptions: [
        { value: 'pt_BR', text: 'Português (Brasil)' },
        { value: 'pt_PT', text: 'Português (Portugal)' },
        { value: 'en_US', text: 'Inglês (EUA)' },
        { value: 'es_ES', text: 'Espanhol (Espanha)' },
        { value: 'es_MX', text: 'Espanhol (México)' },
      ],
      headerTypeOptions: [
        { value: 'NONE', text: 'Sem Header' },
        { value: 'TEXT', text: 'Texto' },
        { value: 'IMAGE', text: 'Imagem (máx. 5MB)' },
        { value: 'VIDEO', text: 'Vídeo (máx. 16MB)' },
      ],
      buttonTypeOptions: [
        { value: 'NONE', text: 'Sem Botões' },
        { value: 'QUICK_REPLY', text: 'Resposta Rápida' },
        { value: 'CALL_TO_ACTION', text: 'Chamada para Ação (URL)' },
        { value: 'ORDER_DETAILS', text: 'Copiar chave Pix' },
      ],
      layoutOptions: [
        { value: 'single', text: 'Template simples (header + corpo + botões)' },
        { value: 'carousel', text: 'Template carrossel (até 10 cards com imagem e botão)' },
      ],
    }
  },
  computed: {
    urlButtonHasUtm() {
      const url = this.newTemplate.urlButton.url || ''
      return /utm_/i.test(url)
    },

    urlButtonUtmSuggested() {
      const url = this.newTemplate.urlButton.url || ''
      if (!url || this.urlButtonHasUtm) return null
      if (this.newTemplate.urlButton.type === 'dynamic') return null
      try {
        const parsed = new URL(url)
        parsed.searchParams.set('utm_source', 'whatsapp')
        parsed.searchParams.set('utm_medium', 'template')
        parsed.searchParams.set('utm_campaign', this._utmCampaignId)
        return parsed.toString()
      } catch {
        return null
      }
    },

    urlButtonTypeOptions() {
      if (this.newTemplate.utilidade === 'campanha') {
        return [{ value: 'static', text: 'URL Estática (fixa)' }]
      }
      return [
        { value: 'static', text: 'URL Estática (fixa)' },
        { value: 'dynamic', text: 'URL Dinâmica (com variável)' },
      ]
    },

    // Mensagem de erro do corpo (regras WhatsApp) para exibir em vermelho abaixo do textarea
    bodyValidationMessage() {
      const result = this.validateBodyWhatsAppRules()
      return result.valid ? '' : result.message
    },

    // Mensagem de erro do corpo no carrossel: não pode ter variáveis
    bodyValidationMessageCarousel() {
      if (this.newTemplate.tipo !== 'customizado' || this.newTemplate.layout !== 'carousel') return ''
      const body = (this.newTemplate.body || '').trim()
      if (!body) return ''
      if (/\{\{[^}]+\}\}/.test(body)) {
        return 'O corpo do template de carrossel não pode conter variáveis. Por enquanto use apenas texto fixo.'
      }
      return ''
    },

    // Header (texto): contém variável? → input vermelho e mensagem
    headerHasVariable() {
      if (this.newTemplate.headerType !== 'TEXT') return false
      return /\{\{[^}]+\}\}/.test(this.newTemplate.headerText || '')
    },
    headerTextState() {
      return this.headerHasVariable ? false : null
    },

    // Rodapé: contém variável? → input vermelho e mensagem
    footerHasVariable() {
      return /\{\{[^}]+\}\}/.test(this.newTemplate.footer || '')
    },
    footerState() {
      return this.footerHasVariable ? false : null
    },

    // Carrossel: 4 passos (sem Botões); senão 5 passos
    stepsToDisplay() {
      const isCarousel = this.newTemplate.tipo === 'customizado' && this.newTemplate.layout === 'carousel'
      return isCarousel ? this.steps.filter(s => s.label !== 'Botões') : this.steps
    },
    currentStepDisplay() {
      const isCarousel = this.newTemplate.tipo === 'customizado' && this.newTemplate.layout === 'carousel'
      return isCarousel && this.currentStep === 5 ? 4 : this.currentStep
    },

    isRecomendacaoProdutosTemplate() {
      return this.newTemplate.layout === 'carousel' && this.newTemplate.name === 'recomendacao_produtos'
    },

    // 🆕 URL do botão para exibição (usa example se disponível)
    urlButtonDisplay() {
      if (!this.templateSelecionado || !this.templateSelecionado.urlButton) return null

      const { urlButton } = this.templateSelecionado

      // Se tem example, usar o primeiro (URL completa)
      if (urlButton.example && urlButton.example.length > 0) {
        return urlButton.example[0]
      }

      // Senão, usar a URL original
      return urlButton.url
    },

    // Variáveis disponíveis no corpo: MARKETING = só Primeiro Nome; carrossel = nenhuma
    availableVariablesForBody() {
      if (this.newTemplate.layout === 'carousel') return []
      if (this.newTemplate.category === 'MARKETING') {
        return this.availableVariables.filter(v => v.key === '{{primeiro_nome}}')
      }
      return this.availableVariables
    },

    // Verifica se deve mostrar o botão "Anterior"
    showPreviousButton() {
      // Mostrar se não está no primeiro step
      if (this.currentStep > 1) return true

      // Mostrar se está no step 1 mas não no primeiro sub-step
      if (this.currentStep === 1 && this.currentSubStep > 1) return true

      // Mostrar se está no step 3 (Conteúdo) mas não no primeiro sub-step
      return false
    },

    // Calcula o progresso visual incluindo sub-steps
    getStepProgress() {
      return stepIndex => {
        const stepNumber = stepIndex + 1

        // Step 1 (Utilidade/Tipo) tem 2 sub-steps
        if (stepNumber === 1) {
          if (this.currentStep > 1) return 100
          if (this.currentStep === 1) {
            return (this.currentSubStep / this.setupSubSteps.length) * 100
          }
          return 0
        }

        // Outros steps: progresso normal
        if (this.currentStep > stepNumber) return 100
        if (this.currentStep === stepNumber) return 100
        return 0
      }
    },

    // Validação para avançar no wizard
    canProceedToNextStep() {
      switch (this.currentStep) {
        case 1: // Nome/Utilidade/Tipo (com 3 sub-steps)
          // Sub-step 1: Nome
          if (this.currentSubStep === 1) {
            return this.newTemplate.name && this.newTemplate.name.length > 0
          }
          // Sub-step 2: Utilidade
          if (this.currentSubStep === 2) {
            return this.newTemplate.utilidade !== null
          }
          // Sub-step 3: Tipo
          if (this.currentSubStep === 3) {
            return this.newTemplate.tipo !== null
          }
          return false
        case 2: // Configurações (Categoria e Idioma)
          return this.newTemplate.category
                 && this.newTemplate.language
        case 3: // Conteúdo (Header, Mensagem e Rodapé ou Carrossel)
          if (this.newTemplate.tipo === 'customizado' && this.newTemplate.layout === 'carousel') {
            if (!this.newTemplate.body || this.newTemplate.body.trim().length === 0) return false
            if (!this.validateBodyWhatsAppRules().valid) return false
            if (!this.newTemplate.carouselButtonText || !this.newTemplate.carouselButtonText.trim()) return false
            if (this.newTemplate.carouselCards.length < 2) return false
            const firstLink = (this.newTemplate.carouselCards[0].productLink || '').trim()
            if (!firstLink || !/^https?:\/\/.+/.test(firstLink)) return false
            const allCardsValid = this.newTemplate.carouselCards.every(
              c => c.image && c.productLink && c.productLink.trim().length > 0,
            )
            return allCardsValid
          }
          if (!this.newTemplate.body || this.newTemplate.body.length === 0) return false
          return this.validateBodyWhatsAppRules().valid && this.validateHeaderFooterNoVariables().valid
        case 4: // Botões (opcional)
          return true
        case 5: // Review
          return true
        default:
          return false
      }
    },

    // Calcula até qual passo o usuário pode navegar diretamente
    maxAccessibleStep() {
      // Passo 1: Verificar se todos os sub-steps estão completos
      const step1Complete = this.newTemplate.name
        && this.newTemplate.name.length > 0
        && this.newTemplate.utilidade !== null
        && this.newTemplate.tipo !== null

      if (!step1Complete) return 1

      // Passo 2: Verificar categoria e idioma
      const step2Complete = this.newTemplate.category
        && this.newTemplate.language

      if (!step2Complete) return 2

      // Passo 3: Corpo obrigatório; se carrossel, validar cards e botão
      let step3Complete = false
      if (this.newTemplate.tipo === 'customizado' && this.newTemplate.layout === 'carousel') {
        const firstLink = (this.newTemplate.carouselCards[0]?.productLink || '').trim()
        const hasValidFirstLink = /^https?:\/\/.+/.test(firstLink)
        const cardsValid = this.newTemplate.carouselCards.length >= 2
          && this.newTemplate.carouselCards.every(
            c => c.image && c.productLink && c.productLink.trim().length > 0,
          )
        step3Complete = this.newTemplate.body
          && this.newTemplate.body.trim().length > 0
          && this.validateBodyWhatsAppRules().valid
          && this.newTemplate.carouselButtonText
          && this.newTemplate.carouselButtonText.trim()
          && hasValidFirstLink
          && cardsValid
      } else {
        step3Complete = this.newTemplate.body
          && this.newTemplate.body.length > 0
          && this.validateBodyWhatsAppRules().valid
          && this.validateHeaderFooterNoVariables().valid
      }

      if (!step3Complete) return 3

      // Carrossel: pular passo 4 (botões), ir direto ao review
      if (this.newTemplate.tipo === 'customizado' && this.newTemplate.layout === 'carousel') {
        return 5
      }

      // Passo 4: Botões são opcionais
      return 5
    },

    filteredTemplates() {
      let filtered = this.templates

      if (this.filterStatus) {
        filtered = filtered.filter(t => t.status === this.filterStatus)
      }

      if (this.filterCategory) {
        filtered = filtered.filter(t => t.category === this.filterCategory)
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(t => t.name.toLowerCase().includes(query))
      }

      this.totalFiltered = filtered.length
      return filtered
    },

    paginatedTemplates() {
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return this.filteredTemplates.slice(start, end)
    },

    startItem() {
      return (this.currentPage - 1) * this.perPage + 1
    },

    endItem() {
      const end = this.currentPage * this.perPage
      return end > this.totalFiltered ? this.totalFiltered : end
    },

    // Filtrar templates pré-definidos: por utilidade (notificação=UTILITY, campanha=MARKETING, retorno=apenas template de retorno) ou pelo dropdown
    predefinedTemplatesFiltered() {
      if (this.newTemplate.utilidade === 'retorno') {
        return this.predefinedTemplates.filter(t => t.utilidade === 'retorno')
      }
      if (this.newTemplate.utilidade === 'notificacao') {
        return this.predefinedTemplates.filter(t => t.category === 'UTILITY' && t.utilidade !== 'retorno')
      }
      if (this.newTemplate.utilidade === 'campanha') {
        return this.predefinedTemplates.filter(t => t.category === 'MARKETING')
      }
      if (this.filtroCategoriaPredefinida) {
        return this.predefinedTemplates.filter(t => t.category === this.filtroCategoriaPredefinida && t.utilidade !== 'retorno')
      }
      return this.predefinedTemplates.filter(t => t.utilidade !== 'retorno')
    },

    // Label do filtro de categoria
    filtroCategoriaLabel() {
      if (!this.filtroCategoriaPredefinida) {
        return 'Todas'
      }
      return this.filtroCategoriaPredefinida === 'UTILITY' ? 'Utilitários' : 'Marketing'
    },
  },
  watch: {
    'newTemplate.name': function (val) {
      if (val) {
        this.newTemplate.name = val
          .toLowerCase()
          .replace(/\s+/g, '_')
      }
    },
    'newTemplate.utilidade': function (val) {
      if (val === 'campanha') {
        this.newTemplate.urlButton.type = 'static'
      }
    },
    'newTemplate.urlButton.url': function (val) {
      if (val && !/utm_/i.test(val)) {
        this._utmCampaignId = this.generateUtmId()
      }
    },
    filterStatus() {
      this.currentPage = 1
    },
    searchQuery() {
      this.currentPage = 1
    },
  },
  mounted() {
    this._utmCampaignId = this.generateUtmId()
    this.facebookCredentials().then(() => {
      this.buscaTemplates()
    })
  },
  methods: {
    generateUtmId() {
      // ULID: 48-bit timestamp (ms) + 80-bit cryptographic randomness, Crockford Base32
      const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
      const now = Date.now()
      let id = ''
      // 10 chars de timestamp (48 bits)
      let t = now
      for (let i = 9; i >= 0; i--) {
        id = ENCODING[t % 32] + id
        t = Math.floor(t / 32)
      }
      // 16 chars de aleatoriedade (80 bits via crypto)
      const rand = crypto.getRandomValues(new Uint8Array(10))
      let r = 0n
      rand.forEach(b => { r = (r << 8n) | BigInt(b) })
      for (let i = 15; i >= 0; i--) {
        id += ENCODING[Number(r % 32n)]
        r >>= 5n
      }
      return id
    },

    // Formatar nome do template automaticamente
    formatTemplateName() {
      if (this.newTemplate.name) {
        this.newTemplate.name = this.newTemplate.name
          .toLowerCase()
          .replace(/\s+/g, '_')
          .replace(/[^a-z0-9_]/g, '')
      }
    },

    // 🆕 Retorna a URL de exibição do botão (usa example se disponível)
    getUrlButtonDisplay(urlButton) {
      if (!urlButton) return null

      // Se tem example, usar o primeiro (URL completa)
      if (urlButton.example && urlButton.example.length > 0) {
        return urlButton.example[0]
      }

      // Senão, usar a URL original
      return urlButton.url
    },

    async copyUrlToClipboard() {
      if (!this.templateSelecionado?.urlButton?.url) return

      try {
        await navigator.clipboard.writeText(this.templateSelecionado.urlButton.url)
        this.showCopiedTooltip = true

        // Esconder o tooltip após 2 segundos
        setTimeout(() => {
          this.showCopiedTooltip = false
        }, 2000)
      } catch (err) {
        console.error('Erro ao copiar link:', err)
        this.$bvToast.toast('Erro ao copiar o link', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000,
        })
      }
    },

    containsEmoji(text) {
      if (!text) return false
      // Regex para detectar emojis (incluindo emojis compostos e variações)
      const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{1F004}\u{1F0CF}\u{1F18E}\u{1F191}-\u{1F19A}\u{1F201}\u{1F21A}\u{1F22F}\u{1F232}-\u{1F236}\u{1F238}-\u{1F23A}\u{1F250}\u{1F251}\u{1F300}-\u{1F320}\u{1F32D}-\u{1F335}\u{1F337}-\u{1F37C}\u{1F37E}-\u{1F393}\u{1F3A0}-\u{1F3CA}\u{1F3CF}-\u{1F3D3}\u{1F3E0}-\u{1F3F0}\u{1F3F4}\u{1F3F8}-\u{1F43E}\u{1F440}\u{1F442}-\u{1F4FC}\u{1F4FF}-\u{1F53D}\u{1F54B}-\u{1F54E}\u{1F550}-\u{1F567}\u{1F57A}\u{1F595}\u{1F596}\u{1F5A4}\u{1F5FB}-\u{1F64F}\u{1F680}-\u{1F6C5}\u{1F6CC}\u{1F6D0}-\u{1F6D2}\u{1F6D5}-\u{1F6D7}\u{1F6EB}\u{1F6EC}\u{1F6F4}-\u{1F6FC}\u{1F7E0}-\u{1F7EB}]/gu
      return emojiRegex.test(text)
    },

    validateNoEmoji(value, fieldName) {
      if (this.containsEmoji(value)) {
        this.$bvToast.toast(
          `O campo "${fieldName}" não pode conter emojis. Emojis são permitidos apenas no corpo da mensagem.`,
          {
            title: 'Emoji não permitido',
            variant: 'warning',
            solid: true,
            autoHideDelay: 4000,
          },
        )
        return false
      }
      return true
    },

    /**
     * Regras WhatsApp para corpo: (variáveis × 3) + 1 ≤ palavras + variáveis;
     * variáveis não no início/fim; duas variáveis não podem estar coladas.
     */
    validateBodyWhatsAppRules() {
      const body = (this.newTemplate.body || '').trim()
      if (!body) return { valid: true }

      // Carrossel: corpo não pode conter variáveis
      if (this.newTemplate.tipo === 'customizado' && this.newTemplate.layout === 'carousel') {
        if (/\{\{[^}]+\}\}/.test(body)) {
          return {
            valid: false,
            message: 'O corpo do template de carrossel não pode conter variáveis. Por enquanto use apenas texto fixo.',
          }
        }
        if (body.length > 1028) {
          return {
            valid: false,
            message: `O corpo da mensagem deve ter no máximo 1028 caracteres. Atual: ${body.length}.`,
          }
        }
        return { valid: true }
      }

      const VAR_REGEX = /\{\{[a-z0-9_]+\}\}/gi
      const vars = body.match(VAR_REGEX) || []
      const numVars = vars.length

      // MARKETING: só é permitida a variável {{primeiro_nome}}
      if (this.newTemplate.category === 'MARKETING' && numVars > 0) {
        const allowedKey = '{{primeiro_nome}}'
        const hasInvalidVar = vars.some(v => v.toLowerCase() !== allowedKey.toLowerCase())
        if (hasInvalidVar) {
          return {
            valid: false,
            message: 'Em templates de campanha (MARKETING) só é permitida a variável "Primeiro Nome" ({{primeiro_nome}}). Remova as demais variáveis do corpo.',
          }
        }
      }

      // Limite de caracteres
      if (body.length > 1028) {
        return {
          valid: false,
          message: `O corpo da mensagem deve ter no máximo 1028 caracteres. Atual: ${body.length}.`,
        }
      }

      if (numVars === 0) return { valid: true }

      // Não permitir variáveis repetidas (mesmo nome mais de uma vez)
      const nomesVars = vars.map(v => v.replace(/\{\{|\}\}/g, '').toLowerCase())
      const unicas = new Set(nomesVars)
      if (unicas.size < nomesVars.length) {
        return {
          valid: false,
          message: 'Não é permitido usar a mesma variável mais de uma vez no corpo da mensagem.',
        }
      }

      // Fórmula: (número de variáveis × 3) + 1 ≤ total de palavras + variáveis
      // Só contam como palavra tokens que tenham pelo menos uma letra (pontuação como "!" não conta)
      const bodySemVars = body.replace(VAR_REGEX, ' ')
      const tokens = bodySemVars.trim().split(/\s+/).filter(Boolean)
      const palavras = tokens.filter(token => /[\p{L}]/u.test(token))
      const totalUnidades = palavras.length + numVars
      const minimoRequerido = numVars * 3 + 1
      if (totalUnidades < minimoRequerido) {
        return {
          valid: false,
          message: `Para ${numVars} variável(is), o corpo precisa de no mínimo ${minimoRequerido} palavras + variáveis (regra WhatsApp). Atual: ${totalUnidades}.`,
        }
      }

      // Variáveis não podem estar no início ou no fim
      if (body.startsWith('{{')) {
        return {
          valid: false,
          message: 'Variáveis não podem estar no início da mensagem. Coloque pelo menos uma palavra antes.',
        }
      }
      if (body.endsWith('}}')) {
        return {
          valid: false,
          message: 'Variáveis não podem estar no fim da mensagem. Coloque pelo menos uma palavra depois.',
        }
      }

      // Duas variáveis não podem estar coladas (precisa pelo menos uma palavra entre elas)
      let lastEnd = -1
      let match
      const re = new RegExp(VAR_REGEX.source, 'gi')
      while ((match = re.exec(body)) !== null) {
        const entre = body.slice(lastEnd + 1, match.index)
        if (lastEnd >= 0 && !/\S/.test(entre)) {
          return {
            valid: false,
            message: 'Não é permitido duas variáveis seguidas. Coloque pelo menos uma palavra entre elas.',
          }
        }
        lastEnd = re.lastIndex - 1
      }
      return { valid: true }
    },

    /** Header e rodapé não podem conter variáveis (máx. 60 caracteres já está no input). */
    validateHeaderFooterNoVariables() {
      if (this.newTemplate.headerType === 'TEXT' && this.newTemplate.headerText) {
        const hasVar = /\{\{[^}]+\}\}/.test(this.newTemplate.headerText)
        if (hasVar) {
          return {
            valid: false,
            message: 'O header não pode conter variáveis. Use apenas texto fixo (máx. 60 caracteres).',
          }
        }
      }
      if (this.newTemplate.footer) {
        const hasVar = /\{\{[^}]+\}\}/.test(this.newTemplate.footer)
        if (hasVar) {
          return {
            valid: false,
            message: 'O rodapé não pode conter variáveis. Use apenas texto fixo (máx. 60 caracteres).',
          }
        }
      }
      return { valid: true }
    },

    async facebookCredentials() {
      const contas = await buscaChatsTeste()
      this.facebookAccountsLista = contas.chats || []

      if (contas.chats.length === 1) {
        this.chatSelecionado = contas.chats[0].chat_token
      } else if (contas.chats.length > 1) {
        this.chatSelecionado = contas.chats[0].chat_token
      } else {
        this.$bvToast.toast('Nenhuma loja encontrada.', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    async transformarTemplates(templates) {
      return templates.map(template => {
        // Detectar se é um template CAROUSEL
        const carouselComponent = template.components?.find(c => c.type === 'CAROUSEL')
        const isCarousel = !!carouselComponent

        // Extrair components
        const headerComponent = template.components?.find(c => c.type === 'HEADER')
        const bodyComponent = template.components?.find(c => c.type === 'BODY')
        const footerComponent = template.components?.find(c => c.type === 'FOOTER')
        const buttonsComponent = template.components?.find(c => c.type === 'BUTTONS')

        // Processar cards do carrossel
        let carouselCards = []
        if (isCarousel && carouselComponent?.cards) {
          carouselCards = carouselComponent.cards.map((card, index) => {
            const cardHeader = card.components?.find(c => c.type === 'HEADER')
            const cardBody = card.components?.find(c => c.type === 'BODY')
            const cardButtons = card.components?.find(c => c.type === 'BUTTONS')

            const buttonRaw = cardButtons?.buttons?.[0]
            const buttonUrlTemplate = buttonRaw?.url || ''
            const buttonUrlExample = buttonRaw?.example?.[0] || ''
            const buttonUrl = buttonUrlTemplate.replace('{{1}}', buttonUrlExample)

            return {
              id: index,
              imageUrl: cardHeader?.example?.header_handle?.[0] || '',
              productName: cardBody?.text || `Produto ${index + 1}`,
              buttonText: buttonRaw?.text || 'Ver produto',
              buttonUrl,
            }
          })
        }

        // Determinar headerType
        let headerType = 'NONE'
        let headerText = ''
        let headerMediaUrl = ''
        if (headerComponent) {
          if (headerComponent.format === 'TEXT') {
            headerType = 'TEXT'
            headerText = headerComponent.text || ''
          } else if (headerComponent.format === 'IMAGE') {
            headerType = 'IMAGE'
            // Extrair URL da imagem do example
            headerMediaUrl = headerComponent.example?.header_handle?.[0] || ''
          } else if (headerComponent.format === 'VIDEO') {
            headerType = 'VIDEO'
            headerMediaUrl = headerComponent.example?.header_handle?.[0] || ''
          } else if (headerComponent.format === 'DOCUMENT') {
            headerType = 'DOCUMENT'
            headerMediaUrl = headerComponent.example?.header_handle?.[0] || ''
          }
        }

        // Extrair buttons
        let buttonType = 'NONE'
        let quickReplyButtons = [{ text: '' }]
        let urlButton = { text: '', url: '', type: 'static' }
        let phoneButton = { text: '', phone: '' }
        let orderDetailsButton = { text: 'Copiar chave Pix' }

        if (buttonsComponent && buttonsComponent.buttons) {
          const firstButton = buttonsComponent.buttons[0]
          if (firstButton) {
            if (firstButton.type === 'QUICK_REPLY') {
              buttonType = 'QUICK_REPLY'
              quickReplyButtons = buttonsComponent.buttons.map(btn => ({ text: btn.text || '' }))
            } else if (firstButton.type === 'URL') {
              buttonType = 'CALL_TO_ACTION'
              urlButton = {
                text: firstButton.text || '',
                url: firstButton.url || '',
                type: firstButton.url && firstButton.url.includes('{{') ? 'dynamic' : 'static',
                example: firstButton.example || null, // 🆕 Preservar example
              }
            } else if (firstButton.type === 'PHONE_NUMBER') {
              buttonType = 'CALL_TO_ACTION'
              phoneButton = {
                text: firstButton.text || '',
                phone: firstButton.phone_number || '',
              }
            } else if (firstButton.type === 'ORDER_DETAILS') {
              buttonType = 'ORDER_DETAILS'
              orderDetailsButton = { text: 'Copiar chave Pix' }
            }
          }
        }

        return {
          id: template.id,
          name: template.name,
          language: template.language,
          category: template.category,
          status: template.status,
          // Campos separados
          layout: isCarousel ? 'carousel' : 'single',
          carouselCards,
          headerType,
          headerText,
          headerMediaUrl,
          body: bodyComponent?.text || '',
          footer: footerComponent?.text || '',
          buttonType,
          quickReplyButtons,
          urlButton,
          phoneButton,
          orderDetailsButton,
          // Manter content como fallback (body)
          content: bodyComponent?.text || template.components[0]?.text || '',
        }
      })
    },

    async buscaTemplates() {
      if (!this.chatSelecionado) return

      this.carregando = true
      try {
        const templates = await listarTemplates(this.chatSelecionado)
        this.templates = await this.transformarTemplates(templates.templates)
      } catch (error) {
        console.error('Erro ao buscar templates:', error)
        this.$bvToast.toast('Erro ao carregar templates', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregando = false
      }
    },

    getStatusVariant(status) {
      const variants = {
        APPROVED: 'success',
        PENDING: 'warning',
        REJECTED: 'danger',
        PAUSED: 'secondary',
      }
      return variants[status] || 'secondary'
    },

    getStatusIcon(status) {
      const icons = {
        APPROVED: 'CheckCircleIcon',
        PENDING: 'ClockIcon',
        REJECTED: 'XCircleIcon',
        PAUSED: 'PauseCircleIcon',
      }
      return icons[status] || 'CircleIcon'
    },

    getStatusText(status) {
      const texts = {
        APPROVED: 'Aprovado',
        PENDING: 'Em Aprovação',
        REJECTED: 'Rejeitado',
        PAUSED: 'Pausado',
      }
      return texts[status] || status
    },

    getLanguageText(language) {
      const languages = {
        pt_BR: 'Português (BR)',
        en_US: 'Inglês (US)',
        es_ES: 'Espanhol',
        pt_PT: 'Português (PT)',
      }
      return languages[language] || language
    },

    getTruncatedContent(content) {
      const maxLength = 150
      if (!content) return ''
      if (content.length <= maxLength) return content
      return `${content.substring(0, maxLength)}...`
    },

    isContentTruncated(content) {
      return content && content.length > 150
    },

    viewTemplate(template) {
      this.templateSelecionado = template
      this.showViewModal = true
    },

    async createTemplate() {
      // Validar se há uma conta selecionada
      if (!this.chatSelecionado) {
        this.$bvToast.toast(
          'Por favor, selecione uma Conta do WhatsApp antes de criar o template',
          { title: 'Conta não selecionada', variant: 'warning', solid: true },
        )
        return
      }

      // Validar emojis nos campos (não permitido exceto no body)
      if (!this.validateNoEmoji(this.newTemplate.name, 'Nome do Template')) return
      if (!this.validateNoEmoji(this.newTemplate.headerText, 'Texto do Header')) return
      if (!this.validateNoEmoji(this.newTemplate.footer, 'Rodapé')) return

      // Validar emojis nos botões
      if (this.newTemplate.buttonType === 'QUICK_REPLY') {
        for (let i = 0; i < this.newTemplate.quickReplyButtons.length; i++) {
          const button = this.newTemplate.quickReplyButtons[i]
          if (button.text && !this.validateNoEmoji(button.text, `Botão de Resposta Rápida ${i + 1}`)) {
            return
          }
        }
      }

      if (this.newTemplate.buttonType === 'CALL_TO_ACTION') {
        if (this.newTemplate.urlButton.text && !this.validateNoEmoji(this.newTemplate.urlButton.text, 'Texto do Botão de URL')) {
          return
        }
        if (this.newTemplate.urlButton.url && !this.validateNoEmoji(this.newTemplate.urlButton.url, 'URL do Botão')) {
          return
        }
      }

      if (this.newTemplate.tipo === 'customizado' && this.newTemplate.layout === 'carousel') {
        if (!this.validateNoEmoji(this.newTemplate.carouselButtonText, 'Texto do botão dos cards')) return
      }

      // Regras WhatsApp: header e rodapé sem variáveis
      const headerFooterCheck = this.validateHeaderFooterNoVariables()
      if (!headerFooterCheck.valid) {
        this.$bvToast.toast(headerFooterCheck.message, {
          title: '❌ Regras do WhatsApp',
          variant: 'warning',
          solid: true,
          autoHideDelay: 6000,
        })
        return
      }

      // Regras WhatsApp: corpo (fórmula variáveis, início/fim, adjacentes, máx. 1028 caracteres)
      const bodyCheck = this.validateBodyWhatsAppRules()
      if (!bodyCheck.valid) {
        this.$bvToast.toast(bodyCheck.message, {
          title: '❌ Regras do WhatsApp (corpo)',
          variant: 'warning',
          solid: true,
          autoHideDelay: 6000,
        })
        return
      }

      // Confirmação de Políticas
      const h = this.$createElement
      const messageVNode = h('div', { class: 'text-left' }, [
        h('p', { class: 'mb-2' }, 'Ao enviar este template para aprovação, você confirma que:'),
        h('ul', { class: 'mb-3' }, [
          h('li', ['✅ Leu e está de acordo com as ', h('strong', 'Políticas do WhatsApp')]),
          h('li', ['✅ O template ', h('strong', 'NÃO contém venda direta'), ' de produtos ou serviços']),
          h('li', ['✅ Evitou usar ', h('strong', 'palavras proibidas'), ' que podem causar banimento']),
          h('li', ['✅ O conteúdo é ', h('strong', 'informativo e útil'), ' para o destinatário']),
        ]),
        h('div', { class: 'alert alert-warning mb-0', style: { fontSize: '0.9rem' } }, [
          h('strong', '⚠️ Atenção: '),
          'Violações podem resultar em ',
          h('strong', 'banimento da conta'),
          '.',
        ]),
      ])

      const confirmado = await this.$bvModal.msgBoxConfirm([messageVNode], {
        title: 'Confirmar Envio do Template',
        size: 'md',
        okVariant: 'success',
        okTitle: 'Sim, estou ciente e de acordo',
        cancelTitle: 'Cancelar',
        cancelVariant: 'outline-secondary',
        centered: true,
      })

      if (!confirmado) {
        return
      }

      try {
        this.criandoTemplate = true

        // ===== VALIDAÇÕES PREVENTIVAS =====

        // 1. Validar formato do nome
        const nomeValido = /^[a-z0-9_]+$/.test(this.newTemplate.name)
        if (!nomeValido) {
          this.$bvToast.toast(
            'O nome do template deve conter apenas letras minúsculas, números e underscores (_).\n\nExemplo: boas_vindas_cliente',
            {
              title: '❌ Nome Inválido',
              variant: 'warning',
              solid: true,
              autoHideDelay: 6000,
            },
          )
          this.criandoTemplate = false
          return
        }

        // ===== FLUXO CARROSSEL =====
        if (this.newTemplate.tipo === 'customizado' && this.newTemplate.layout === 'carousel') {
          const cards = this.newTemplate.carouselCards
          if (cards.length < 2 || cards.length > 10) {
            this.$bvToast.toast('O carrossel deve ter entre 2 e 10 cards.', { title: 'Carrossel inválido', variant: 'warning', solid: true })
            this.criandoTemplate = false
            return
          }
          for (let i = 0; i < cards.length; i++) {
            if (!cards[i].image || !cards[i].productLink || !cards[i].productLink.trim()) {
              this.$bvToast.toast(`Card ${i + 1}: envie uma imagem e preencha o link do produto.`, { title: 'Card incompleto', variant: 'warning', solid: true })
              this.criandoTemplate = false
              return
            }
          }
          if (!this.newTemplate.carouselButtonText || !this.newTemplate.carouselButtonText.trim()) {
            this.$bvToast.toast('Informe o texto do botão dos cards.', { title: 'Campo obrigatório', variant: 'warning', solid: true })
            this.criandoTemplate = false
            return
          }

          this.uploadingMedia = true
          const headerHandles = []
          try {
            for (let i = 0; i < cards.length; i++) {
              this.$bvToast.toast(`Enviando imagem do card ${i + 1}/${cards.length}...`, { title: 'Upload', variant: 'info', solid: true })
              const res = await uploadTemplateMedia(cards[i].image, this.chatSelecionado)
              const handle = res.media_id || res.handle
              if (!handle) throw new Error(`Sem header_handle no card ${i + 1}`)
              headerHandles.push(handle)
            }
          } catch (err) {
            console.error('Erro upload mídia carrossel:', err)
            this.$bvToast.toast('Erro ao enviar imagens dos cards. Tente novamente.', { title: 'Erro no upload', variant: 'danger', solid: true })
            this.uploadingMedia = false
            this.criandoTemplate = false
            return
          } finally {
            this.uploadingMedia = false
          }

          const bodyComponent = {
            type: 'body',
            text: this.newTemplate.body,
          }
          const bodyVars = (this.newTemplate.body.match(/\{\{[a-z_]+\}\}/g) || [])
          if (bodyVars.length > 0) {
            bodyComponent.example = {
              body_text_named_params: bodyVars.map(v => {
                const varName = v.replace(/\{\{|\}\}/g, '')
                return {
                  param_name: varName,
                  example: this.getVariableExample(varName),
                }
              }),
            }
          }

          const carouselCards = cards.map((card, idx) => {
            const fullUrl = (card.productLink || '').trim().replace(/\/+$/, '')
            const qIndex = fullUrl.indexOf('?')
            const pathPart = qIndex !== -1 ? fullUrl.slice(0, qIndex) : fullUrl
            const queryPart = qIndex !== -1 ? fullUrl.slice(qIndex) : ''
            const lastSlash = pathPart.lastIndexOf('/')
            const protocolEnd = pathPart.indexOf('://') + 3
            const hasPath = lastSlash > protocolEnd
            const base = hasPath ? pathPart.slice(0, lastSlash) : pathPart
            const suffix = hasPath ? pathPart.slice(lastSlash + 1) : pathPart
            return {
              components: [
                {
                  type: 'header',
                  format: 'image',
                  example: { header_handle: [headerHandles[idx]] },
                },
                {
                  type: 'buttons',
                  buttons: [
                    {
                      type: 'url',
                      text: this.newTemplate.carouselButtonText.trim(),
                      url: `${base}/{{1}}`,
                      example: [`${suffix}${queryPart}`],
                    },
                  ],
                },
              ],
            }
          })

          const components = [
            bodyComponent,
            {
              type: 'carousel',
              cards: carouselCards,
            },
          ]

          const templateData = {
            name: this.newTemplate.name,
            language: this.newTemplate.language,
            category: this.newTemplate.category,
            parameter_format: 'NAMED',
            components,
            utilidade: this.newTemplate.utilidade,
          }

          try {
            await criarTemplate(this.chatSelecionado, templateData)
            this.$bvToast.toast('Template de carrossel criado e enviado para aprovação do WhatsApp!', {
              title: 'Sucesso', variant: 'success', solid: true, autoHideDelay: 5000,
            })
            this.resetForm()
            this.showCreateModal = false
            await this.buscaTemplates()
          } catch (error) {
            console.error('Erro ao criar template carrossel:', error)
            const errorMessage = this.interpretarErroWhatsApp(error)
            this.$bvToast.toast(errorMessage.mensagem, {
              title: errorMessage.titulo, variant: 'danger', solid: true, autoHideDelay: 8000,
            })
          } finally {
            this.criandoTemplate = false
          }
          return
        }

        // 2. Validar URLs dos botões
        if (this.newTemplate.buttons && this.newTemplate.buttons.length > 0) {
          for (let i = 0; i < this.newTemplate.buttons.length; i++) {
            const button = this.newTemplate.buttons[i]

            if (button.type === 'URL') {
              const url = button.url?.trim()

              // Verificar se URL está vazia
              if (!url) {
                this.$bvToast.toast(
                  `O botão "${button.text}" está configurado como URL mas não possui uma URL definida.\n\nSolução:\n• Adicione uma URL válida\n• Ou remova o botão`,
                  {
                    title: '❌ URL Vazia',
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 7000,
                  },
                )
                this.criandoTemplate = false
                return
              }

              // Verificar se é uma URL válida (deve começar com http:// ou https://)
              const isVariable = url.startsWith('{{') && url.endsWith('}}')
              const isValidUrl = /^https?:\/\/.+/.test(url)

              if (!isVariable && !isValidUrl) {
                this.$bvToast.toast(
                  `A URL do botão "${button.text}" está inválida.\n\nFormatos aceitos:\n• URL completa: https://exemplo.com\n• Variável: {{link_de_rastreio}}\n\nURL atual: ${url}`,
                  {
                    title: '❌ URL Inválida',
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 8000,
                  },
                )
                this.criandoTemplate = false
                return
              }
            }
          }
        }

        // 3. Validar mídia obrigatória
        if (['IMAGE', 'VIDEO'].includes(this.newTemplate.headerType) && !this.newTemplate.headerMedia) {
          this.$bvToast.toast(
            `É obrigatório fazer upload ${this.newTemplate.headerType === 'IMAGE' ? 'da imagem' : 'do vídeo'} para este tipo de header`,
            { title: 'Mídia Obrigatória', variant: 'warning', solid: true },
          )
          this.criandoTemplate = false
          return
        }

        // Upload de mídia do header (se houver)
        let headerMediaHandle = null
        if (this.newTemplate.headerMedia && ['IMAGE', 'VIDEO'].includes(this.newTemplate.headerType)) {
          this.uploadingMedia = true
          try {
            this.$bvToast.toast(
              'Fazendo upload da mídia... Aguarde.',
              { title: 'Uploading', variant: 'info', solid: true },
            )

            const mediaResponse = await uploadTemplateMedia(
              this.newTemplate.headerMedia,
              this.chatSelecionado,
            )

            headerMediaHandle = mediaResponse.media_id || mediaResponse.handle

            this.$bvToast.toast(
              'Mídia enviada com sucesso!',
              { title: 'Upload Completo', variant: 'success', solid: true },
            )
          } catch (error) {
            console.error('Erro ao fazer upload da mídia:', error)
            this.$bvToast.toast(
              'Erro ao fazer upload da mídia. Tente novamente.',
              { title: 'Erro no Upload', variant: 'danger', solid: true },
            )
            this.uploadingMedia = false
            return
          } finally {
            this.uploadingMedia = false
          }
        }

        // Montar a estrutura de components do WhatsApp Cloud API
        const components = []

        // HEADER (se houver)
        if (this.newTemplate.headerType !== 'NONE') {
          const headerComponent = {
            type: 'HEADER',
            format: this.newTemplate.headerType,
          }

          if (this.newTemplate.headerType === 'TEXT') {
            headerComponent.text = this.newTemplate.headerText

            // Extrair variáveis descritivas do header
            const headerVars = (this.newTemplate.headerText.match(/\{\{[a-z_]+\}\}/g) || [])
            if (headerVars.length > 0) {
              // Gerar exemplos reais para cada variável
              const headerExamples = headerVars.map(v => {
                const varName = v.replace(/\{\{|\}\}/g, '')
                return this.getVariableExample(varName)
              })

              headerComponent.example = {
                header_text: headerExamples,
              }
            }
          } else if (['IMAGE', 'VIDEO'].includes(this.newTemplate.headerType) && headerMediaHandle) {
            // Adicionar handle da mídia ao header
            headerComponent.example = {
              header_handle: [headerMediaHandle],
            }
          }

          components.push(headerComponent)
        }

        // BODY (obrigatório)
        const bodyComponent = {
          type: 'BODY',
          text: this.newTemplate.body,
        }

        // Extrair variáveis descritivas do body
        const bodyVars = (this.newTemplate.body.match(/\{\{[a-z_]+\}\}/g) || [])
        if (bodyVars.length > 0) {
          // Gerar exemplos reais para cada variável
          const bodyExamples = bodyVars.map(v => {
            const varName = v.replace(/\{\{|\}\}/g, '')
            return this.getVariableExample(varName)
          })

          bodyComponent.example = {
            body_text: [bodyExamples],
          }
        }

        components.push(bodyComponent)

        // FOOTER (se houver)
        if (this.newTemplate.footer) {
          components.push({
            type: 'FOOTER',
            text: this.newTemplate.footer,
          })
        }

        // BUTTONS (se houver)
        if (this.newTemplate.buttonType !== 'NONE') {
          const buttons = []

          if (this.newTemplate.buttonType === 'QUICK_REPLY') {
            // Quick Reply Buttons
            this.newTemplate.quickReplyButtons
              .filter(btn => btn.text.trim())
              .forEach(btn => {
                buttons.push({
                  type: 'QUICK_REPLY',
                  text: btn.text.trim(),
                })
              })
          } else if (this.newTemplate.buttonType === 'CALL_TO_ACTION') {
            // URL Button
            if (this.newTemplate.urlButton.text && this.newTemplate.urlButton.url) {
              let finalUrl = this.newTemplate.urlButton.url
              if (this.newTemplate.urlButton.type === 'static' && this.urlButtonUtmSuggested) {
                finalUrl = this.urlButtonUtmSuggested
              }
              const urlButton = {
                type: 'URL',
                text: this.newTemplate.urlButton.text,
                url: finalUrl,
              }

              // Se for URL dinâmica, extrair variáveis e gerar examples
              if (this.newTemplate.urlButton.type === 'dynamic') {
                // Gerar URL de exemplo substituindo as variáveis por valores de exemplo
                let urlExample = this.newTemplate.urlButton.url

                // Encontrar todas as variáveis na URL
                const urlVars = (this.newTemplate.urlButton.url.match(/\{\{[a-z_]+\}\}/g) || [])

                // Substituir cada variável por seu valor de exemplo
                urlVars.forEach(v => {
                  const varName = v.replace(/\{\{|\}\}/g, '')
                  // Obter apenas o valor simples, não a URL completa
                  const exampleValue = this.getSimpleExampleValue(varName)
                  urlExample = urlExample.replace(v, exampleValue)
                })

                // O example deve ser um array com a URL completa como exemplo
                urlButton.example = [urlExample]

                console.log('🔗 Botão URL dinâmico criado:', {
                  url_original: this.newTemplate.urlButton.url,
                  url_exemplo: urlExample,
                  estrutura_final: urlButton,
                })
              }

              buttons.push(urlButton)
            }
          } else if (this.newTemplate.buttonType === 'ORDER_DETAILS') {
            // Texto fixo para API (usuário vê "Copiar chave Pix" na interface)
            buttons.push({
              type: 'ORDER_DETAILS',
              text: 'Copy Pix code',
            })
          }

          if (buttons.length > 0) {
            components.push({
              type: 'BUTTONS',
              buttons,
            })
          }
        }

        // Montar payload final
        const templateData = {
          name: this.newTemplate.name,
          language: this.newTemplate.language,
          category: this.newTemplate.category,
          components,
          // Metadados adicionais para controle interno
          utilidade: this.newTemplate.utilidade, // 'retorno', 'notificacao', 'campanha'
        }

        console.log('📤 Enviando template para API:', {
          accountId: this.chatSelecionado,
          templateData,
        })

        await criarTemplate(this.chatSelecionado, templateData)

        this.$bvToast.toast('Template criado e enviado para aprovação do WhatsApp!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
          autoHideDelay: 5000,
        })

        this.resetForm()
        this.showCreateModal = false

        // Recarregar templates
        await this.buscaTemplates()
      } catch (error) {
        console.error('Erro ao criar template:', error)

        // Interpretar erro e fornecer mensagem específica
        const errorMessage = this.interpretarErroWhatsApp(error)

        this.$bvToast.toast(errorMessage.mensagem, {
          title: errorMessage.titulo,
          variant: 'danger',
          solid: true,
          autoHideDelay: 8000,
        })
      } finally {
        this.criandoTemplate = false
      }
    },

    // Interpretar erros da API do WhatsApp
    interpretarErroWhatsApp(error) {
      const errorData = error.response?.data
      const errorDetails = errorData?.detalhes?.error || errorData?.error
      const errorMessage = errorDetails?.message || error.message
      const errorCode = errorDetails?.code

      console.log('📛 Detalhes do erro:', {
        errorData,
        errorDetails,
        errorMessage,
        errorCode,
      })

      // Erros específicos do WhatsApp
      if (errorMessage) {
        // Erro de URL inválida nos botões
        if (errorMessage.includes('is not a valid URI') || errorMessage.includes('url')) {
          return {
            titulo: '❌ URL Inválida',
            mensagem: 'A URL do botão está inválida. Certifique-se de:\n• Usar uma URL completa (ex: https://exemplo.com)\n• Usar variáveis corretas (ex: {{link_de_rastreio}})\n• Não deixar a URL vazia se o botão estiver ativo',
          }
        }

        // Erro de nome duplicado
        if (errorMessage.includes('duplicate') || errorMessage.includes('already exists') || errorCode === 100) {
          return {
            titulo: '❌ Nome Duplicado',
            mensagem: 'Já existe um template com este nome. Escolha um nome diferente.',
          }
        }

        // Erro de formato de nome
        if (errorMessage.includes('name') && (errorMessage.includes('invalid') || errorMessage.includes('format'))) {
          return {
            titulo: '❌ Nome Inválido',
            mensagem: 'O nome do template está em formato inválido.\n\nRegras:\n• Apenas letras minúsculas\n• Números e underscores (_)\n• Sem espaços ou caracteres especiais\n\nExemplo correto: boas_vindas_cliente',
          }
        }

        // Erro de variáveis
        if (errorMessage.includes('variable') || errorMessage.includes('parameter')) {
          return {
            titulo: '❌ Erro nas Variáveis',
            mensagem: 'Há um problema com as variáveis do template.\n\nVerifique:\n• Formato correto: {{nome_variavel}}\n• Variáveis suportadas pelo WhatsApp\n• Correspondência entre variáveis no texto e nos botões',
          }
        }

        // Erro de categoria
        if (errorMessage.includes('category')) {
          return {
            titulo: '❌ Categoria Inválida',
            mensagem: 'A categoria do template está inválida.\n\nUse:\n• UTILITY - para notificações e atualizações\n• MARKETING - para promoções e ofertas',
          }
        }

        // Erro de mídia
        if (errorMessage.includes('media') || errorMessage.includes('image') || errorMessage.includes('video')) {
          return {
            titulo: '❌ Erro na Mídia',
            mensagem: 'Há um problema com o arquivo de mídia.\n\nVerifique:\n• Formato suportado (JPG, PNG para imagens)\n• Tamanho máximo (5MB para imagens)\n• URL acessível publicamente',
          }
        }

        // Erro de limite de templates
        if (errorMessage.includes('limit') || errorMessage.includes('quota')) {
          return {
            titulo: '❌ Limite Atingido',
            mensagem: 'Você atingiu o limite de templates.\n\nSolução:\n• Delete templates não utilizados\n• Aguarde aprovação dos templates pendentes\n• Entre em contato com o suporte do WhatsApp',
          }
        }

        // Erro genérico com mensagem da API
        return {
          titulo: '❌ Erro ao Criar Template',
          mensagem: `${errorMessage}\n\nSe o erro persistir, entre em contato com o suporte.`,
        }
      }

      // Erro genérico sem detalhes
      return {
        titulo: '❌ Erro Desconhecido',
        mensagem: 'Não foi possível criar o template. Verifique sua conexão e tente novamente.\n\nSe o erro persistir, entre em contato com o suporte.',
      }
    },

    resetForm() {
      this.currentStep = 1
      this.currentSubStep = 1
      this.newTemplate = {
        utilidade: null,
        tipo: null,
        layout: 'single',
        name: '',
        language: 'pt_BR',
        category: 'UTILITY',
        headerType: 'NONE',
        headerText: '',
        headerMedia: null,
        headerMediaPreview: null,
        body: '',
        footer: '',
        buttonType: 'NONE',
        quickReplyButtons: [{ text: '' }],
        urlButton: { text: '', url: '', type: 'static' },
        phoneButton: { text: '', phone: '' },
        orderDetailsButton: { text: 'Copiar chave Pix' },
        carouselCards: [
          { image: null, imagePreview: null, productLink: '' },
          { image: null, imagePreview: null, productLink: '' },
        ],
        carouselButtonText: '',
      }
      if (this.$refs.headerMediaInput) {
        this.$refs.headerMediaInput.value = ''
      }
      // Limpar refs de upload de cards do carrossel
      this.$nextTick(() => {
        const inputs = this.$el.querySelectorAll('input[data-carousel-card-input]')
        if (inputs && inputs.length) {
          inputs.forEach(input => { input.value = '' })
        }
      })
    },

    // Métodos do Wizard Step-by-Step
    nextStep() {
      // Step 1 (Utilidade/Tipo) tem sub-steps
      if (this.currentStep === 1 && this.currentSubStep < this.setupSubSteps.length) {
        this.currentSubStep++
        this.scrollToTop()
      }
      // Carrossel: do passo 3 ir direto ao 5 (pular botões)
      else if (this.canProceedToNextStep && this.currentStep === 3
        && this.newTemplate.tipo === 'customizado' && this.newTemplate.layout === 'carousel') {
        this.currentStep = 5
        this.currentSubStep = 1
        this.scrollToTop()
      }
      // Avançar para próximo step
      else if (this.canProceedToNextStep && this.currentStep < this.steps.length) {
        this.currentStep++
        this.currentSubStep = 1
        this.scrollToTop()
      }
    },

    previousStep() {
      // Step 1 (Utilidade/Tipo) tem sub-steps
      if (this.currentStep === 1 && this.currentSubStep > 1) {
        this.currentSubStep--
        this.scrollToTop()
      }
      // Carrossel: do passo 5 voltar para o 3 (pular botões)
      else if (this.currentStep === 5
        && this.newTemplate.tipo === 'customizado' && this.newTemplate.layout === 'carousel') {
        this.currentStep = 3
        this.currentSubStep = 1
        this.scrollToTop()
      }
      // Voltar para step anterior
      else if (this.currentStep > 1) {
        this.currentStep--
        if (this.currentStep === 1) {
          this.currentSubStep = this.setupSubSteps.length
        } else {
          this.currentSubStep = 1
        }
        this.scrollToTop()
      }
    },

    goToStep(stepNumber) {
      // Permitir navegar para:
      // 1. Qualquer passo anterior ao atual (voltar)
      // 2. Qualquer passo até o máximo acessível (avançar se preenchido)
      if (stepNumber <= this.maxAccessibleStep) {
        this.currentStep = stepNumber
        // Resetar sub-step
        this.currentSubStep = 1
        this.scrollToTop()
      } else {
        // Mostrar feedback se tentar pular para passo não acessível
        this.$bvToast.toast(
          'Complete os passos anteriores antes de avançar para este passo.',
          {
            title: 'Passo Bloqueado',
            variant: 'warning',
            solid: true,
            autoHideDelay: 3000,
          },
        )
      }
    },

    // Para o stepper: passo "exibido" 4 no carrossel = passo real 5
    getActualStepForDisplay(displayStep) {
      const isCarousel = this.newTemplate.tipo === 'customizado' && this.newTemplate.layout === 'carousel'
      return isCarousel && displayStep === 4 ? 5 : displayStep
    },
    getStepProgressDisplay(stepIndex) {
      const actualStepIndex = this.getActualStepForDisplay(stepIndex + 1) - 1
      return this.getStepProgress(actualStepIndex)
    },

    scrollToTop() {
      this.$nextTick(() => {
        const formElement = this.$el.querySelector('.modal-form-scrollable')
        if (formElement) {
          formElement.scrollTop = 0
        }
      })
    },

    handleStepSubmit() {
      // Avançar para próxima etapa (não criar template aqui)
      this.nextStep()
    },

    clearCurrentStep() {
      switch (this.currentStep) {
        case 1: // Nome/Utilidade/Tipo
          if (this.currentSubStep === 1) {
            this.newTemplate.name = ''
          } else if (this.currentSubStep === 2) {
            this.newTemplate.utilidade = null
          } else if (this.currentSubStep === 3) {
            this.newTemplate.tipo = null
          }
          break
        case 2: // Configurações (Categoria e Idioma)
          this.newTemplate.category = 'UTILITY'
          this.newTemplate.language = 'pt_BR'
          break
        case 3: // Conteúdo (Header, Mensagem e Rodapé)
          // Limpar tudo do conteúdo
          this.newTemplate.headerType = 'NONE'
          this.newTemplate.headerText = ''
          this.newTemplate.headerMedia = null
          this.newTemplate.headerMediaPreview = null
          this.newTemplate.body = ''
          this.newTemplate.footer = ''
          if (this.$refs.headerMediaInput) {
            this.$refs.headerMediaInput.value = ''
          }
          break
        case 4: // Botões
          this.newTemplate.buttonType = 'NONE'
          this.newTemplate.quickReplyButtons = [{ text: '' }]
          this.newTemplate.urlButton = { text: '', url: '', type: 'static' }
          this.newTemplate.phoneButton = { text: '', phone: '' }
          break
        case 5: // Revisar (não limpa nada)
          break
      }
    },

    getLanguageLabel(lang) {
      const option = this.languageOptions.find(opt => opt.value === lang)
      return option ? option.text : lang
    },

    getHeaderTypeLabel(type) {
      const option = this.headerTypeOptions.find(opt => opt.value === type)
      return option ? option.text : type
    },

    getButtonTypeLabel(type) {
      const option = this.buttonTypeOptions.find(opt => opt.value === type)
      return option ? option.text : type
    },

    // Inserir variável no body
    insertVariable(variableKey) {
      const textarea = this.$refs.bodyTextarea.$el
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const text = this.newTemplate.body

      // Inserir a variável na posição do cursor
      this.newTemplate.body = text.substring(0, start) + variableKey + text.substring(end)

      // Reposicionar o cursor após a variável inserida
      this.$nextTick(() => {
        const newPosition = start + variableKey.length
        textarea.setSelectionRange(newPosition, newPosition)
        textarea.focus()
      })
    },

    // Inserir variável no body (quando está no layout carrossel)
    insertVariableInBody(variableKey) {
      const ref = this.$refs.bodyTextareaCarousel
      if (!ref) return
      const textarea = Array.isArray(ref) ? ref[0].$el : ref.$el
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const text = this.newTemplate.body
      this.newTemplate.body = text.substring(0, start) + variableKey + text.substring(end)
      this.$nextTick(() => {
        const newPosition = start + variableKey.length
        textarea.setSelectionRange(newPosition, newPosition)
        textarea.focus()
      })
    },

    addCarouselCard() {
      if (this.newTemplate.carouselCards.length < 10) {
        this.newTemplate.carouselCards.push({ image: null, imagePreview: null, productLink: '' })
      }
    },
    removeCarouselCard(index) {
      if (this.newTemplate.carouselCards.length > 2) {
        const card = this.newTemplate.carouselCards[index]
        if (card.imagePreview) URL.revokeObjectURL(card.imagePreview)
        this.newTemplate.carouselCards.splice(index, 1)
      }
    },
    handleCarouselCardImage(event, index) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      const card = this.newTemplate.carouselCards[index]
      if (card.imagePreview) URL.revokeObjectURL(card.imagePreview)
      card.image = file
      card.imagePreview = URL.createObjectURL(file)
      event.target.value = ''
    },
    removeCarouselCardImage(index) {
      const card = this.newTemplate.carouselCards[index]
      if (card.imagePreview) URL.revokeObjectURL(card.imagePreview)
      card.image = null
      card.imagePreview = null
      card.productLink = ''
      const inputRef = this.$refs[`carouselCardInput_${index}`]
      const input = Array.isArray(inputRef) ? inputRef[0] : inputRef
      if (input) input.value = ''
    },
    openCarouselCardFile(index) {
      const inputRef = this.$refs[`carouselCardInput_${index}`]
      const input = Array.isArray(inputRef) ? inputRef[0] : inputRef
      if (input) input.click()
    },

    // Inserir variável na URL
    insertUrlVariable(variableKey) {
      const currentUrl = this.newTemplate.urlButton.url || ''

      // Se a URL estiver vazia, adiciona a variável diretamente
      if (!currentUrl) {
        this.newTemplate.urlButton.url = variableKey
        return
      }

      // Se já tiver conteúdo, adiciona ao final
      this.newTemplate.urlButton.url = currentUrl + variableKey
    },

    // Upload de mídia do header
    handleHeaderMediaUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      const maxSize = this.newTemplate.headerType === 'IMAGE' ? 5 * 1024 * 1024 : 16 * 1024 * 1024 // 5MB ou 16MB
      const allowedTypes = this.newTemplate.headerType === 'IMAGE'
        ? ['image/jpeg', 'image/png']
        : ['video/mp4', 'video/avi', 'video/quicktime']

      // Validar tipo
      if (!allowedTypes.includes(file.type)) {
        this.$bvToast.toast(
          `Tipo de arquivo inválido. Use ${this.newTemplate.headerType === 'IMAGE' ? 'JPG ou PNG' : 'MP4 ou AVI'}`,
          { title: 'Erro', variant: 'danger', solid: true },
        )
        event.target.value = ''
        return
      }

      // Validar tamanho
      if (file.size > maxSize) {
        const maxSizeMB = this.newTemplate.headerType === 'IMAGE' ? '5MB' : '16MB'
        this.$bvToast.toast(
          `O arquivo excede o tamanho máximo de ${maxSizeMB}`,
          { title: 'Erro', variant: 'danger', solid: true },
        )
        event.target.value = ''
        return
      }

      // Criar preview
      this.newTemplate.headerMedia = file
      const reader = new FileReader()

      reader.onload = e => {
        this.newTemplate.headerMediaPreview = e.target.result
      }

      reader.readAsDataURL(file)

      this.$bvToast.toast(
        `${this.newTemplate.headerType === 'IMAGE' ? 'Imagem' : 'Vídeo'} carregado com sucesso!`,
        { title: 'Sucesso', variant: 'success', solid: true },
      )
    },

    // Remover mídia do header
    removeHeaderMedia() {
      this.newTemplate.headerMedia = null
      this.newTemplate.headerMediaPreview = null
      if (this.$refs.headerMediaInput) {
        this.$refs.headerMediaInput.value = ''
      }
    },

    closeCreateModal() {
      this.resetForm()
      this.showCreateModal = false
    },

    // Carregar template pré-definido
    loadPredefinedTemplate(template) {
      const utilidadeAtual = this.newTemplate.utilidade
      const tipoAtual = this.newTemplate.tipo

      if (template.layout === 'carousel') {
        this.newTemplate = {
          utilidade: utilidadeAtual,
          tipo: 'customizado',
          layout: 'carousel',
          name: this.newTemplate.name,
          language: 'pt_BR',
          category: template.category || 'MARKETING',
          headerType: 'NONE',
          headerText: '',
          headerMedia: null,
          headerMediaPreview: null,
          body: template.body || '',
          footer: '',
          buttonType: 'NONE',
          quickReplyButtons: [{ text: '' }],
          urlButton: { text: '', url: '', type: 'static' },
          phoneButton: { text: '', phone: '' },
          orderDetailsButton: { text: 'Copiar chave Pix' },
          carouselCards: [
            { image: null, imagePreview: null, productLink: '' },
            { image: null, imagePreview: null, productLink: '' },
          ],
          carouselButtonText: 'Acessar',
        }
      } else {
        this.newTemplate = {
          utilidade: utilidadeAtual,
          tipo: tipoAtual,
          layout: 'single',
          name: this.newTemplate.name,
          language: 'pt_BR',
          category: template.category,
          headerType: template.headerType,
          headerText: template.headerText || '',
          headerMedia: null,
          headerMediaPreview: null,
          body: template.body,
          footer: template.footer || '',
          buttonType: template.buttonType,
          quickReplyButtons: template.quickReplyButtons ? [...template.quickReplyButtons] : [{ text: '' }],
          urlButton: template.urlButton ? { ...template.urlButton } : { text: '', url: '', type: 'static' },
          phoneButton: template.phoneButton ? { ...template.phoneButton } : { text: '', phone: '' },
          orderDetailsButton: template.orderDetailsButton ? { ...template.orderDetailsButton } : { text: 'Copiar chave Pix' },
          carouselCards: [
            { image: null, imagePreview: null, productLink: '' },
            { image: null, imagePreview: null, productLink: '' },
          ],
          carouselButtonText: '',
        }
      }

      this.$bvToast.toast(
        `Template "${this.formatTemplateName(template.name)}" carregado! Personalize conforme necessário.`,
        { title: 'Template Carregado', variant: 'success', solid: true },
      )

      // Scroll para o topo do formulário
      this.$nextTick(() => {
        const formElement = this.$el.querySelector('.modal-form-scrollable')
        if (formElement) {
          formElement.scrollTop = 0
        }
      })
    },

    // Formatar nome do template
    formatTemplateName(name) {
      if (name === 'carousel_recomendacao_produtos') return 'Carrossel de Produtos'
      return name
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    // Ícone do template
    getTemplateIcon(template) {
      const icons = {
        pedido_em_transporte: 'TruckIcon',
        pedido_aprovado: 'CheckCircleIcon',
        pedido_entregue: 'PackageIcon',
        pedido_cancelado: 'XCircleIcon',
        carrinho_abandonado: 'ShoppingCartIcon',
        cashback_disponivel: 'DollarSignIcon',
        pedido_pix: 'CopyIcon',
        campanha_video: 'VideoIcon',
        campanha_imagem: 'ImageIcon',
        carousel_recomendacao_produtos: 'LayoutIcon',
        recomendacao_produtos: 'PackageIcon',
      }
      return icons[template.name] || 'FileTextIcon'
    },

    // Gerar valor de exemplo para variáveis descritivas
    getVariableExample(varName) {
      const examples = {
        primeiro_nome: 'Maria',
        email: 'maria@email.com',
        numero_pedido: '12345',
        total_pedido: 'R$ 299,90',
        valor_total: '199,90',
        data_entrega: '25/10/2025',
        link_do_boleto: 'https://sualoja.com/boleto/12345',
        codigo_do_boleto: '23793.38128 60236.506203 87654.321098',
        link_de_rastreio: 'https://sualoja.com/rastreio/BR123456789',
        link_de_avaliacao: 'https://sualoja.com/avaliar/12345',
        link_do_carrinho: 'https://sualoja.com/carrinho/ABC123',
        codigo_de_rastreio: 'BR123456789',
      }

      return examples[varName] || 'Exemplo'
    },

    // Gerar apenas o valor simples (sem URL completa) para substituir em URLs dinâmicas
    getSimpleExampleValue(varName) {
      const simpleExamples = {
        primeiro_nome: 'Maria',
        email: 'maria@email.com',
        numero_pedido: '12345',
        total_pedido: '299.90',
        data_entrega: '25-10-2025',
        link_do_boleto: '12345',
        codigo_do_boleto: '23793381286023650620387654321098',
        link_de_rastreio: 'BR123456789',
        link_de_avaliacao: '12345',
        link_do_carrinho: 'ABC123',
        codigo_de_rastreio: 'BR123456789',
        codigo_cupom: 'DESC10',
        id_produto: 'PROD123',
        token: 'abc123def456',
      }

      return simpleExamples[varName] || 'exemplo123'
    },

    // Limpar todos os campos
    clearAllFields() {
      this.$bvModal.msgBoxConfirm(
        'Tem certeza que deseja limpar todos os campos do formulário?',
        {
          title: 'Limpar Campos',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Sim, limpar',
          cancelTitle: 'Cancelar',
          centered: true,
        },
      ).then(confirmado => {
        if (confirmado) {
          this.resetForm()
          this.$bvToast.toast(
            'Todos os campos foram limpos com sucesso!',
            { title: 'Campos Limpos', variant: 'info', solid: true },
          )

          // Scroll para o topo
          this.$nextTick(() => {
            const formElement = this.$el.querySelector('.modal-form-scrollable')
            if (formElement) {
              formElement.scrollTop = 0
            }
          })
        }
      })
    },

    // Métodos para gerenciar botões Quick Reply
    addQuickReplyButton() {
      if (this.newTemplate.quickReplyButtons.length < 3) {
        this.newTemplate.quickReplyButtons.push({ text: '' })
      }
    },

    removeQuickReplyButton(index) {
      if (this.newTemplate.quickReplyButtons.length > 1) {
        this.newTemplate.quickReplyButtons.splice(index, 1)
      }
    },

    // Helpers para labels
    getCategoryLabel(value) {
      const option = this.categoryOptions.find(o => o.value === value)
      return option ? option.text : value
    },

    getLanguageLabel(value) {
      const option = this.languageOptions.find(o => o.value === value)
      return option ? option.text : value
    },

    confirmDelete(template) {
      this.$bvModal
        .msgBoxConfirm(`Tem certeza que deseja deletar o template "${template.name}"?`, {
          title: 'Confirmar exclusão',
          okTitle: 'Sim',
          cancelTitle: 'Cancelar',
          okVariant: 'danger',
        })
        .then(value => {
          if (value) this.deleteTemplate(template.name)
        })
    },

    deleteTemplate(templateName) {
      this.templates = this.templates.filter(t => t.name !== templateName)
      excluirTemplate(this.chatSelecionado, templateName)
      this.$bvToast.toast('Template deletado com sucesso!', {
        title: 'Removido',
        variant: 'danger',
        solid: true,
      })
    },
  },
}
</script>

<style scoped>
/* ============================================
   WIZARD STEPPER STYLES
   ============================================ */
.template-stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  margin-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.stepper-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  cursor: pointer;
  transition: all 0.3s ease;
}

.stepper-item.active .stepper-circle {
  background: linear-gradient(135deg, #9A3092 0%, #c44ab8 100%);
  color: white;
  border-color: #9A3092;
  box-shadow: 0 0 0 4px rgba(154, 48, 146, 0.1);
  transform: scale(1.1);
}

.stepper-item.completed .stepper-circle {
  background: #28a745;
  color: white;
  border-color: #28a745;
}

.stepper-item.active .stepper-label small {
  color: #9A3092;
  font-weight: 600;
}

/* Passos bloqueados (não acessíveis) */
.stepper-item.blocked {
  opacity: 0.5;
  cursor: not-allowed;
}

.stepper-item.blocked .stepper-circle {
  background: #f8f9fa;
  border-color: #dee2e6;
  color: #adb5bd;
}

.stepper-item.blocked .stepper-label small {
  color: #adb5bd;
}

/* Passos acessíveis (podem ser clicados) */
.stepper-item.accessible {
  cursor: pointer;
}

.stepper-item.accessible:hover:not(.active) .stepper-circle {
  border-color: #9A3092;
  transform: scale(1.05);
  transition: all 0.2s ease;
}

.stepper-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  border: 2px solid #dee2e6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  z-index: 2;
  position: relative;
}

.stepper-label {
  margin-top: 0.5rem;
  text-align: center;
}

.stepper-label small {
  font-size: 0.75rem;
  color: #6c757d;
  white-space: nowrap;
}

.sub-step-indicator {
  display: inline-block;
  margin-left: 0.25rem;
  padding: 0.125rem 0.375rem;
  background: #9A3092;
  color: white;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1;
}

.stepper-line {
  position: absolute;
  top: 20px;
  left: 50%;
  right: -50%;
  height: 2px;
  background: #dee2e6;
  z-index: 1;
  overflow: hidden;
}

.stepper-line-progress {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #9A3092;
  transition: width 0.3s ease;
}

.stepper-item.completed .stepper-line {
  background: #dee2e6;
}

.stepper-item.completed .stepper-line-progress {
  width: 100% !important;
  background: #28a745;
}

/* Sub-Steps Progress */
.sub-steps-progress {
  padding: 1rem 0;
}

.progress-bar-container {
  width: 100%;
  height: 4px;
  background: #e9ecef;
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #7367f0 0%, #9e95f5 100%);
  transition: width 0.3s ease;
  border-radius: 2px;
}

.sub-steps-dots {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.sub-step-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: #6c757d;
  position: relative;
  z-index: 2;
}

.sub-step-dot.active {
  background: #7367f0;
  color: white;
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.4);
}

.sub-step-dot.completed {
  background: #28a745;
  color: white;
}

/* Step Headers */
.step-header {
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f0f0;
}

.step-title {
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.section-subtitle {
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-top: 1.5rem;
}

.step-header p {
  margin-bottom: 0;
  color: #6c757d;
}

/* Review Section */
.review-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
}

.review-item {
  padding: 0.75rem;
  background: white;
  border-radius: 6px;
  border-left: 3px solid #9A3092;
}

.review-item label {
  color: #9A3092;
  font-size: 0.875rem;
}

/* Responsive Stepper */
@media (max-width: 768px) {
  .template-stepper {
    overflow-x: auto;
    justify-content: flex-start;
    padding-bottom: 1rem;
  }

  .stepper-item {
    min-width: 80px;
  }

  .stepper-circle {
    width: 32px;
    height: 32px;
    font-size: 0.75rem;
  }

  .stepper-label small {
    font-size: 0.65rem;
  }
}

/* Container Principal */
.templates-whatsapp-container {
  padding-bottom: 2rem;
}

/* Header Fixo ao Scroll */
.header-section {
  position: relative;
  top: 0;
  z-index: 100;
  background: #f8f9fa;
  padding: 0;
  margin-bottom: 1rem;
}

.header-card {
  background: linear-gradient(135deg, #9A3092 0%, #c44ab8 100%);
  border: none;
  box-shadow: 0 8px 24px rgba(154, 48, 146, 0.25);
  margin-bottom: 0;
  border-radius: 16px;
  animation: slideInDown 0.5s ease;
}

.header-card >>> .card-body {
  padding: 2rem 1.5rem;
}

.header-info .page-title {
  color: white;
  font-weight: 600;
  display: flex;
  align-items: center;
  font-size: 1.75rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-info .page-subtitle {
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.95rem;
  font-weight: 400;
}

.btn-create {
  background: white;
  color: #9A3092;
  font-weight: 600;
  padding: 0.75rem 1.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 8px;
}

.btn-create:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  background: #f8f9fa;
  color: #9A3092;
}

.btn-create:active {
  transform: translateY(0);
}

/* Animação do Header */
@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Filters Card - Rola Normalmente */
.filters-card {
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin: 0 1rem 1rem 1rem;
}

/* Conteúdo */
.content-section {
  padding: 0 1rem 1rem 1rem;
}

/* Templates Grid */
.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  padding: 1rem 0;
  align-items: stretch;
}

/* Garante que todos os cards tenham a mesma altura */
.templates-grid > * {
  display: flex;
  flex-direction: column;
}

.template-card {
  transition: all 0.2s ease;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  overflow: visible;
  height: 100%;
  min-height: 380px;
  display: flex;
  flex-direction: column;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.template-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
}

/* Status Border Colors */
.template-card.status-approved {
  border-top: 3px solid #28c76f;
}

.template-card.status-pending {
  border-top: 3px solid #ff9f43;
}

.template-card.status-rejected {
  border-top: 3px solid #ea5455;
}

/* Card Body Padding Uniform */
.template-card >>> .card-body {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Template Header */
.template-header {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  flex-shrink: 0;
}

.template-name h5 {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  max-width: 100%;
  height: 1.3rem;
}

.template-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  align-items: center;
}

.template-meta .badge {
  font-size: 0.75rem;
  padding: 0.35rem 0.65rem;
  font-weight: 500;
}

/* Template Content - WhatsApp Preview */
.template-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.whatsapp-preview {
  background: linear-gradient(135deg, #e5ddd5 0%, #d9d1c9 100%);
  padding: 1rem;
  border-radius: 8px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.preview-bubble {
  background: white;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 200px;
  max-height: 350px;
  overflow: hidden;
}

.preview-bubble p {
  color: #2c3e50;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 0.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.template-content .template-header {
  color: #1a1a1a;
  font-size: 0.875rem;
  font-weight: 600;
  padding-bottom: 0.5rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.header-type-badge {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1976d2;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
}

.template-preview-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;
  width: 100%;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.template-preview-content:has(.template-header-preview:empty) {
  display: block;
}

.template-preview-content:has(.template-header-preview strong) {
  display: block;
}

.template-header-preview {
  flex-shrink: 0;
}

.template-header-preview strong {
  font-size: 0.9rem;
  color: #1a1a1a;
  display: block;
}

.template-body-preview {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.template-body-preview p {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.template-footer-preview {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #f0f0f0;
}

.template-footer-preview small {
  font-size: 0.75rem;
  color: #999;
}

.template-buttons-preview {
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.button-preview {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  color: #128c7e;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
}

.button-preview:hover {
  background: #e9ecef;
}

.button-url {
  color: #0088cc;
}

.button-phone {
  color: #128c7e;
}

.button-url-info {
  margin-top: 0.25rem;
  padding: 0.25rem 0.5rem;
  text-align: center;
  font-size: 0.75rem;
  word-break: break-all;

  small {
    color: #6c757d;
    opacity: 0.8;
  }
}

.button-url-link {
  margin-top: 0.4rem;
  padding: 0.4rem 0.5rem;
  background: #f1f3f5;
  border-radius: 4px;
  text-align: center;
  font-size: 0.7rem;
  word-break: break-all;
  border: 1px solid #dee2e6;

  small {
    color: #0088cc;
    font-weight: 500;
  }
}

.template-header-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
}

.template-header-video {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
}

.template-header-image-full {
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
  display: block;
}

.template-header-video-full {
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 300px;
  border-radius: 8px;
  display: block;
}

.preview-bubble::before {
  content: '';
  position: absolute;
  top: 0;
  left: -8px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 8px 8px 0;
  border-color: transparent white transparent transparent;
}

.preview-bubble p {
  font-size: 0.9rem;
  color: #303030;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.ver-mais {
  color: #9A3092;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  margin-top: 0.5rem;
  transition: all 0.2s ease;
  align-self: flex-end;
}

.ver-mais:hover {
  color: #7a2574;
  text-decoration: underline;
}

/* Dropdown Actions */
.template-card .dropdown-toggle {
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.template-card .dropdown-toggle:hover {
  background: #f8f9fa;
}

.template-card .dropdown-menu {
  border: 1px solid #e9ecef;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 0.5rem;
}

.template-card .dropdown-item {
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.template-card .dropdown-item:hover {
  background: #f8f9fa;
  transform: translateX(4px);
}

/* Paginação */
.pagination-section {
  padding: 1.5rem 0;
  margin-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.pagination-info {
  font-weight: 500;
  color: #6c757d;
}

/* Badges Padronizados */
.badge {
  border-radius: 6px;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.badge-light-secondary {
  background: #e9ecef;
  color: #6c757d;
}

.badge-light-info {
  background: #d1ecf1;
  color: #0c5460;
}

/* Estados de Loading e Empty */
.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  min-height: 400px;
}

.empty-state h5 {
  color: #6c757d;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.empty-state p {
  color: #adb5bd;
  margin-bottom: 1.5rem;
}

/* Responsive */
@media (max-width: 768px) {
  .templates-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .template-card {
    min-height: 320px;
  }

  .template-name h5 {
    font-size: 0.95rem;
    min-height: 2.4rem;
  }

  .preview-bubble {
    min-height: 180px;
  }
}

@media (max-width: 576px) {
  .template-meta {
    gap: 0.25rem;
  }

  .template-meta .badge {
    font-size: 0.7rem;
    padding: 0.25rem 0.5rem;
  }
}

/* Badges Antigos */
.badge-old {
  font-weight: 500;
  padding: 0.35rem 0.65rem;
  font-size: 0.75rem;
}

/* Empty State */
.empty-state {
  background: white;
  border-radius: 8px;
  border: 2px dashed #e9ecef;
}

/* Dropdown Menu */
.dropdown-menu {
  border: 1px solid #e9ecef;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dropdown-item {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}

.dropdown-item:hover {
  background: #f8f9fa;
}

/* Modal Styles */
.view-meta {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.whatsapp-preview-full {
  background: #e5ddd5;
  padding: 1.5rem;
  border-radius: 12px;
  min-height: 150px;
}

.whatsapp-message-container {
  max-width: 65%;
  margin-left: 0;
}

/* Mídia Header (Imagem/Vídeo) - Fora da bubble como no WhatsApp */
.whatsapp-media-header {
  margin-bottom: 2px;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
  background: white;
}

.whatsapp-media-image {
  width: 100%;
  height: auto;
  max-height: 400px !important;
  object-fit: cover;
  display: block;
}

.whatsapp-media-video {
  width: 100%;
  height: auto;
  max-height: 400px;
  display: block;
}

/* Bubble do WhatsApp */
.whatsapp-bubble {
  background: white;
  padding: 8px 10px 6px;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
  position: relative;
}

.whatsapp-message-container:not(:has(.whatsapp-media-header)) .whatsapp-bubble {
  border-radius: 8px;
}

.whatsapp-message-container:not(:has(.whatsapp-media-header)) .whatsapp-bubble::before {
  content: '';
  position: absolute;
  top: 0;
  left: -8px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 8px 8px 0;
  border-color: transparent white transparent transparent;
}

.whatsapp-header-text {
  display: block;
  font-size: 1rem;
  color: #000;
  font-weight: 600;
  margin-bottom: 4px;
  line-height: 1.4;
}

.whatsapp-body-text {
  font-size: 0.9375rem;
  color: #303030;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  padding-right: 50px;
}

.whatsapp-footer-text {
  display: block;
  font-size: 0.8125rem;
  color: #667781;
  margin-top: 4px;
  line-height: 1.4;
}

.whatsapp-timestamp {
  position: absolute;
  bottom: 6px;
  right: 10px;
  font-size: 0.6875rem;
  color: #667781;
  display: flex;
  align-items: center;
  gap: 3px;
}

/* Botões do WhatsApp */
.whatsapp-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}

.whatsapp-button {
  background: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #00a5f4;
  cursor: pointer;
  transition: background 0.2s;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
  width: 100%;
}

.whatsapp-button:hover {
  background: #f5f6f6;
}

.whatsapp-button:active {
  background: #e9edef;
}

.whatsapp-button-reply {
  color: #00a5f4;
}

.whatsapp-button-url {
  color: #00a5f4;
}

.whatsapp-button-phone {
  color: #00a5f4;
}

.button-url-display {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  text-align: center;
  font-size: 0.8rem;
  word-break: break-all;
  border: 1px solid #e9ecef;

  small {
    color: #0088cc;
    font-weight: 500;
  }
}

.whatsapp-button svg {
  flex-shrink: 0;
}

/* Animação de texto deslizante no botão */
.whatsapp-button-animated {
  overflow: hidden;
}

.button-text-wrapper {
  position: relative;
  display: inline-block;
  height: 1.2em;
  overflow: hidden;
  min-width: 150px;
}

.button-text-original,
.button-text-hover {
  display: block;
  transition: transform 0.3s ease, opacity 0.3s ease;
  white-space: nowrap;
}

.button-text-original {
  transform: translateY(0);
  opacity: 1;
}

.button-text-hover {
  position: absolute;
  top: 100%;
  left: 0;
  width: max-content;
  min-width: 150px;
  transform: translateY(0);
  opacity: 0;
}

.whatsapp-button-animated:hover .button-text-original {
  transform: translateY(-100%);
  opacity: 0;
}

.whatsapp-button-animated:hover .button-text-hover {
  transform: translateY(-100%);
  opacity: 1;
}

/* Container do botão URL com tooltip */
.url-button-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Tooltip de link copiado */
.copied-tooltip {
  position: absolute;
  bottom: -35px;
  background: #25d366;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
  z-index: 1000;
}

.copied-tooltip::before {
  content: '';
  position: absolute;
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 6px 4px 6px;
  border-color: transparent transparent #25d366 transparent;
}

/* Animação de fade */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

code {
  background: #f4f4f4;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #e83e8c;
}

.gap-2 {
  gap: 0.5rem;
}

/* Code Tags for Variables */
code {
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.85em;
  color: #d63384;
  font-family: 'Courier New', monospace;
  font-weight: 600;
}

/* Variables Buttons */
.variables-buttons-container {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.variables-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.5rem;
}

.variable-btn {
  font-size: 0.8rem;
  padding: 0.4rem 0.6rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.2s;
}

.variable-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(115, 103, 240, 0.2);
}

/* Header Media Upload */
.upload-zone-header {
  border: 2px dashed #d8d6de;
  border-radius: 8px;
  padding: 2rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.upload-zone-header:hover {
  border-color: #7367f0;
  background: #f8f7ff;
}

.media-preview-container-header {
  position: relative;
}

.media-preview-header {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
}

.preview-img-header,
.preview-video-header {
  width: 100%;
  max-height: 250px;
  object-fit: contain;
  display: block;
}

.media-actions-header {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

/* Carrossel: upload por card — linha horizontal, máx 3 visíveis, scroll */
.carousel-cards-row {
  display: flex;
  overflow-x: auto;
  gap: 1rem;
  padding: 0.5rem 0;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}
.carousel-cards-row::-webkit-scrollbar {
  height: 8px;
}
.carousel-cards-row::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
.carousel-cards-row::-webkit-scrollbar-thumb {
  background: #7367f0;
  border-radius: 4px;
}
.carousel-card-editor-wrap {
  flex: 0 0 280px;
  min-width: 280px;
  scroll-snap-align: start;
}
.upload-zone-carousel {
  cursor: pointer;
  background: #f8f9fa;
  transition: all 0.2s ease;
}
.upload-zone-carousel:hover {
  border-color: #7367f0 !important;
  background: #f8f7ff;
}
.carousel-card-editor {
  border: 1px solid #e9ecef;
}
.carousel-card-preview {
  border-radius: 8px;
}

/* Carrossel em bloco separado do corpo (estilo campaign/inteligentes/recomendacao) */
.carousel-preview-standalone {
  margin-top: 0.75rem;
}
.carousel-preview-standalone .carousel-scroll-one-and-quarter {
  display: flex;
  overflow-x: auto;
  gap: 0.75rem;
  padding: 0.5rem 0;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}
.carousel-preview-standalone .carousel-scroll-one-and-quarter::-webkit-scrollbar {
  height: 6px;
}
.carousel-preview-standalone .carousel-scroll-one-and-quarter::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}
.carousel-preview-standalone .carousel-scroll-one-and-quarter::-webkit-scrollbar-thumb {
  background: #7367f0;
  border-radius: 4px;
}
/* 1 card inteiro + 1/4 do próximo visível */
.carousel-preview-standalone .carousel-item-preview {
  flex: 0 0 80%;
  min-width: 200px;
  max-width: 220px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  scroll-snap-align: start;
}
.carousel-preview-standalone .carousel-image {
  width: 100%;
  height: 160px;
  background: linear-gradient(135deg, #7367f0 0%, #5e50ee 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}
.carousel-preview-standalone .carousel-image-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.carousel-preview-standalone .carousel-info {
  padding: 1rem;
}
.carousel-preview-standalone .product-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
}
.carousel-preview-standalone .carousel-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.carousel-preview-standalone .carousel-btn,
.carousel-preview-standalone .carousel-btn-link {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border: 1px solid #7367f0;
  border-radius: 6px;
  color: #7367f0;
  font-size: 0.85rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s;
}
.carousel-preview-standalone .carousel-btn-link:hover {
  background: rgba(115, 103, 240, 0.1);
  color: #5e50ee;
  text-decoration: none;
}

/* Aviso Recomendação de Produtos (dados de exemplo para Meta) */
.recomendacao-produtos-aviso {
  border-left: 4px solid #ff9f43;
  font-size: 0.9rem;
}

/* Templates Pré-definidos */
/* Filtro de Categoria */
.filtro-categoria-dropdown {
  margin-left: auto;
}

.filtro-categoria-dropdown .dropdown-toggle {
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
}

.filtro-categoria-dropdown .dropdown-menu {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.filtro-categoria-dropdown .dropdown-item {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.filtro-categoria-dropdown .dropdown-item:hover {
  background: #f8f9fa;
  transform: translateX(4px);
}

/* ===== UTILIDADE OPTIONS ===== */
.utilidade-options,
.tipo-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.utilidade-card,
.tipo-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.utilidade-card:hover,
.tipo-card:hover {
  border-color: #7367f0;
  background: #f8f9fa;
  transform: translateX(4px);
}

.utilidade-card.selected,
.tipo-card.selected {
  border-width: 2px;
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.05) 0%, rgba(115, 103, 240, 0.1) 100%);
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15);
}

.utilidade-card.border-primary { border-color: #7367f0 !important; }
.utilidade-card.border-warning { border-color: #ff9f43 !important; }
.utilidade-card.border-success { border-color: #28c76f !important; }
.tipo-card.border-info { border-color: #00cfe8 !important; }
.tipo-card.border-secondary { border-color: #82868b !important; }

.utilidade-icon,
.tipo-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.utilidade-content,
.tipo-content {
  flex: 1;
}

.utilidade-content h5,
.tipo-content h5 {
  font-weight: 600;
  color: #5e5873;
}

.utilidade-check,
.tipo-check {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

.check-placeholder {
  width: 24px;
  height: 24px;
  border: 2px solid #e9ecef;
  border-radius: 50%;
}

/* Background colors for icons */
.bg-light-primary {
  background: rgba(115, 103, 240, 0.12);
}

.bg-light-warning {
  background: rgba(255, 159, 67, 0.12);
}

.bg-light-success {
  background: rgba(40, 199, 111, 0.12);
}

.bg-light-info {
  background: rgba(0, 207, 232, 0.12);
}

.bg-light-secondary {
  background: rgba(130, 134, 139, 0.12);
}

/* Responsive */
@media (max-width: 576px) {
  .utilidade-card,
  .tipo-card {
    padding: 1rem;
  }

  .utilidade-icon,
  .tipo-icon {
    width: 48px;
    height: 48px;
  }

  .utilidade-content h5,
  .tipo-content h5 {
    font-size: 0.95rem;
  }
}

.predefined-templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
}

.predefined-card {
  background: #fff;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 120px;
  display: flex;
  flex-direction: column;
}

.predefined-card:hover {
  border-color: #7367f0;
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.2);
}

.predefined-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.predefined-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #5e5873;
  margin-bottom: 0.4rem;
  line-height: 1.3;
}

.predefined-description {
  font-size: 0.75rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

@media (max-width: 768px) {
  .predefined-templates-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .predefined-templates-grid {
    grid-template-columns: 1fr;
  }

  .form-section .d-flex.justify-content-between {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .form-section .d-flex.justify-content-between .btn {
    margin-top: 0.5rem;
    width: 100%;
  }
}

/* Modal de Criação */
.modal-body-no-scroll {
  overflow: hidden !important;
}

.modal-form-scrollable {
  overflow-y: auto;
  max-height: 75vh;
  scroll-behavior: smooth;
}

.modal-form-scrollable::-webkit-scrollbar {
  width: 6px;
}

.modal-form-scrollable::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modal-form-scrollable::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.modal-form-scrollable::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.modal-preview-scrollable {
  overflow-y: auto;
  max-height: 75vh;
  scroll-behavior: smooth;
}

.modal-preview-scrollable::-webkit-scrollbar {
  width: 6px;
}

.modal-preview-scrollable::-webkit-scrollbar-track {
  background: #e9ecef;
  border-radius: 10px;
}

.modal-preview-scrollable::-webkit-scrollbar-thumb {
  background: #adb5bd;
  border-radius: 10px;
}

.modal-preview-scrollable::-webkit-scrollbar-thumb:hover {
  background: #6c757d;
}

.form-section {
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 1rem;
}

.form-section:last-of-type {
  border-bottom: none;
}

.section-title {
  color: #5e5873;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

/* Preview Section */
.preview-section {
  background: #f8f9fa;
  height: 100%;
}

.whatsapp-phone-preview {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 100%;
}

.phone-header {
  background: #075e54;
}

.phone-status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  color: white;
  font-size: 0.75rem;
}

.phone-icons {
  display: flex;
  gap: 0.5rem;
}

.phone-chat-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #128c7e;
  color: white;
}

.phone-messages {
  padding: 1rem;
  background: #e5ddd5;
  min-height: 300px;
  max-height: 400px;
  overflow-y: auto;
}

.message-bubble {
  background: white;
  border-radius: 8px;
  padding: 0;
  max-width: 100%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.message-header {
  margin-bottom: 0.5rem;
}

.header-text {
  padding: 0.75rem;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.95rem;
  background: #ffffff;
}

.header-media {
  background: #f8f9fa;
  min-height: 120px;
  position: relative;
  overflow: hidden;
}

.media-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: #f0f0f0;
  border-bottom: 1px solid #e0e0e0;
  min-height: 120px;
}

.header-media-preview-img {
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: cover;
  display: block;
  border-radius: 8px 8px 0 0;
}

.header-media-preview-video {
  width: 100%;
  height: auto;
  max-height: 200px;
  display: block;
  border-radius: 8px 8px 0 0;
}

.message-body {
  padding: 0.75rem;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 60px;
  display: block !important;
}

.message-body p {
  margin: 0;
  color: #303030;
  line-height: 1.4;
  display: block !important;
}

.message-footer {
  padding: 0 0.75rem 0.5rem 0.75rem;
  border-top: 1px solid #f0f0f0;
  margin-top: 0.5rem;
}

.message-buttons {
  border-top: 1px solid #e9ecef;
  padding: 0.5rem 0;
}

.button-item {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem;
  color: #128c7e;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.button-item:last-child {
  border-bottom: none;
}

.button-url-link-modal {
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  text-align: center;
  font-size: 0.75rem;
  word-break: break-all;
  margin: 0.5rem 0.75rem;

  small {
    color: #0088cc;
    font-weight: 500;
  }
}

.button-item:hover {
  background: #f8f9fa;
}

.message-time {
  text-align: right;
  padding: 0.25rem 0.75rem 0.5rem 0.75rem;
}

.template-info {
  position: sticky;
  bottom: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .header-card >>> .card-body {
    padding: 1.5rem 1rem;
  }

  .header-info .page-title {
    font-size: 1.35rem;
  }

  .header-info .page-subtitle {
    font-size: 0.85rem;
  }

  .filters-card {
    margin: 0 0.5rem 1rem 0.5rem;
  }

  .content-section {
    padding: 0 0.5rem 1rem 0.5rem;
  }

  .templates-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .btn-create {
    width: 100%;
    justify-content: center;
    margin-top: 0.5rem;
  }

  .header-actions {
    width: 100%;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .templates-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1025px) {
  .templates-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1400px) {
  .templates-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Modal de Políticas - Compacto */
::v-deep .policies-modal-body {
  max-height: 70vh;
  overflow-y: auto;
  padding: 1rem;
  scroll-behavior: smooth;
}

.policies-content {
  font-size: 0.875rem;
}

.policy-section {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 6px;
  border-left: 3px solid #7367f0;
}

.policy-section p {
  margin-bottom: 0.5rem;
}

.policy-section .mb-2 {
  margin-bottom: 0.5rem !important;
}

.policy-section .mb-3 {
  margin-bottom: 0.75rem !important;
}

.policy-title {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #5e5873;
  font-size: 0.95rem;
}

.policy-list {
  margin-bottom: 0;
  padding-left: 1.5rem;
  font-size: 0.85rem;
}

.policy-list li {
  margin-bottom: 0.4rem;
  line-height: 1.5;
}

.policy-list li:last-child {
  margin-bottom: 0;
}

.policies-content .alert {
  padding: 0.75rem;
  margin-bottom: 0.5rem;
}

.policies-content .alert small {
  font-size: 0.85rem;
}

/* Comparison Box - Compacto */
.comparison-box {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.comparison-wrong,
.comparison-right {
  padding: 0.6rem;
  border-radius: 4px;
  border: 2px solid;
}

.comparison-wrong {
  background: #fff5f5;
  border-color: #ea5455;
}

.comparison-wrong strong {
  color: #ea5455;
  font-size: 0.85rem;
}

.comparison-right {
  background: #f0fff4;
  border-color: #28c76f;
}

.comparison-right strong {
  color: #28c76f;
  font-size: 0.85rem;
}

.comparison-wrong small,
.comparison-right small {
  font-size: 0.8rem;
  line-height: 1.5;
}

/* Scrollbar customizado para o modal de políticas */
.policies-modal-body::-webkit-scrollbar {
  width: 6px;
}

.policies-modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.policies-modal-body::-webkit-scrollbar-thumb {
  background: #7367f0;
  border-radius: 4px;
}

.policies-modal-body::-webkit-scrollbar-thumb:hover {
  background: #5e50ee;
}

@media (max-width: 768px) {
  .comparison-box {
    grid-template-columns: 1fr;
  }

  ::v-deep .policies-modal-body {
    max-height: 65vh;
  }
}

/* Overlay de Loading do Modal */
::v-deep .modal-content {
  position: relative;
}

.modal-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  border-radius: 0.5rem;
}

.loading-content {
  text-align: center;
  padding: 2rem;
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Utility Classes for Gap */
.gap-2 {
  gap: 0.5rem;
}

/* ============================================= */
/* ESTILOS PARA VISUALIZAÇÃO DO CARROSSEL */
/* ============================================= */

/* Carrossel Compacto - Preview no Card */
.carousel-preview-compact {
  position: relative;
  margin-top: 0.5rem;
}

.carousel-scroll-horizontal {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 0.5rem 0;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}

.carousel-scroll-horizontal::-webkit-scrollbar {
  height: 4px;
}

.carousel-scroll-horizontal::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.carousel-scroll-horizontal::-webkit-scrollbar-thumb {
  background: #7367f0;
  border-radius: 2px;
}

.carousel-card-compact {
  flex: 0 0 120px;
  min-width: 120px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
}

.carousel-card-image {
  width: 100%;
  height: 80px;
  overflow: hidden;
  background: #f0f0f0;
}

.carousel-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carousel-card-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: white;
}

.carousel-card-action {
  padding: 0.4rem;
  text-align: center;
  font-size: 0.7rem;
  color: #7367f0;
  font-weight: 500;
  border-top: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  overflow: hidden;
}

.carousel-card-link {
  padding: 0 0.4rem 0.4rem;
  text-align: center;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.carousel-card-link small {
  font-size: 0.65rem;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.carousel-count-badge {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: rgba(115, 103, 240, 0.9);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Carrossel no Modal - Preview Completo */
.carousel-modal-preview {
  margin-top: 1rem;
}

.carousel-scroll-modal {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 1rem 0;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}

.carousel-scroll-modal::-webkit-scrollbar {
  height: 8px;
}

.carousel-scroll-modal::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.carousel-scroll-modal::-webkit-scrollbar-thumb {
  background: #7367f0;
  border-radius: 4px;
}

.carousel-card-modal {
  flex: 0 0 240px;
  min-width: 240px;
  max-width: 240px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  scroll-snap-align: start;
  transition: transform 0.2s ease;
}

.carousel-card-modal:hover {
  transform: translateY(-4px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.carousel-card-image-modal {
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #f0f0f0;
}

.carousel-card-image-modal img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.carousel-card-placeholder-modal {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: white;
}

.carousel-card-info-modal {
  padding: 1rem;
}

.carousel-card-button-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  border: 1px solid #7367f0;
  border-radius: 8px;
  color: #7367f0;
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
}

.carousel-card-button-modal:hover {
  background: rgba(115, 103, 240, 0.1);
  color: #5e50ee;
  text-decoration: none;
  transform: translateY(-1px);
}

.carousel-card-button-modal.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.carousel-card-url-display {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e9ecef;
  text-align: center;
}

.carousel-card-url-display small {
  font-size: 0.75rem;
  word-break: break-all;
  display: block;
}

/* Responsive adjustments for step navigation */
@media (max-width: 576px) {
  .step-title {
    font-size: 1.25rem;
  }

  .step-header p {
    font-size: 0.875rem;
  }
}
</style>
